import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Highcharts from "highcharts";
import FunnelModule from "highcharts/modules/funnel";
import { Chart } from "@highcharts/react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Moment from "moment";
import Swal from "sweetalert2";
import { Circles } from "react-loader-spinner";
import { Card, CardHeader, CardBody } from "./../../components/card/card.jsx";
import "bootstrap-daterangepicker/daterangepicker.css";
import "moment/locale/es";

Moment.locale("es");

const loadHighchartsModule = (module) => {
  if (typeof module === "function") {
    module(Highcharts);
    return;
  }

  if (module && typeof module.default === "function") {
    module.default(Highcharts);
  }
};

loadHighchartsModule(FunnelModule);

// ---------------------------------------------------------------------------
// Rango de carga
// ---------------------------------------------------------------------------
// El dashboard arranca con el mes actual (del día 1 a hoy) y a la API solo
// se le pide el rango seleccionado (parámetros startDate / endDate que ya
// existían). El mes anterior para los comparativos se consulta aparte y el
// histórico del año solo cuando se pide con su botón.
const ETIQUETA_RANGO_DEFAULT = "mes actual";

const getRangoDefault = () => ({
  startDate: Moment().startOf("month").format("YYYY-MM-DD"),
  endDate: Moment().format("YYYY-MM-DD"),
});

// ---------------------------------------------------------------------------
// Matriz de agencias (Grupo → Razón social → Dirección → Agencia)
// ---------------------------------------------------------------------------
// venta_financiamiento no guarda grupo / razón social / dirección, así que se
// obtienen de esta matriz cruzando por ag_id (y, si no hay ag_id o no está en
// la lista, por el nombre de la agencia). Es la misma matriz que ya usa
// /ventas/vehiculo/exportar-datos en index.php ($AGENCIA_CATALOGO).
// Si se da de alta una agencia nueva o cambia una dirección, actualizar aquí.
const SIN_ASIGNAR = "(Sin asignar)";

const AGENCIAS_MATRIZ = [
  // ---- Gocar ----
  { agIds: ["ag0532_5598bb1c503e6"], agencia: "Nissan Aragon", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  { agIds: ["ag0532_5598bb1c86d5a"], agencia: "Nissan Santa Clara", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  { agIds: ["ag5713_5c33d949e445e"], agencia: "Nissan Texcoco", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  { agIds: ["ag5912_5c33d9c0c10df"], agencia: "Nissan Tecamac", grupo: "Gocar", razonSocial: "Spanicar Mexico", direccion: "Gabriel Muñoz" },
  { agIds: ["ag1150_5b44ccb6362b8", "ag4931_691e032bd8757"], agencia: "MG Ecatepec", grupo: "Gocar", razonSocial: "Transace", direccion: "Edgar Pacheco" },
  { agIds: ["ag3307_631f6d5397531"], agencia: "Chirey Ecatepec", grupo: "Gocar", razonSocial: "Gocar Oriente", direccion: "Edgar Pacheco" },
  { agIds: ["ag0533_5598bb1d621ab"], agencia: "Renault Universidad", grupo: "Gocar", razonSocial: "Euro France", direccion: "Jose Gomez Aguilar" },
  { agIds: ["ag0532_5598bb1c4042b"], agencia: "Renault San Angel", grupo: "Gocar", razonSocial: "Euro France", direccion: "Jose Gomez Aguilar" },
  { agIds: ["ag0532_5598bb1c51d78"], agencia: "VW", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  { agIds: ["ag5348_691e042c59a67"], agencia: "Seat", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  { agIds: ["ag3651_663021039f3f2"], agencia: "Cupra", grupo: "Gocar", razonSocial: "Leon Motors", direccion: "Francisco Tarazona" },
  { agIds: ["ag0532_5598bb1c56605", "ag0931_6500d38be2bc6", "ag1035_6500d3cb15aed"], agencia: "Seminuevos Leon", grupo: "Gocar", razonSocial: "Gocar Bajio", direccion: "Francisco Tarazona" },
  // ---- Sunroad ----
  { agIds: ["ag2327_620bfdaf30656"], agencia: "MG Toluca", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Sur", direccion: "Edgar Pacheco" },
  { agIds: ["ag3636_61d5d72451639"], agencia: "MG Metepec", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Sur", direccion: "Edgar Pacheco" },
  { agIds: ["ag5650_5e20f842e21a6"], agencia: "Honda Tollocan", grupo: "Sunroad", razonSocial: "Camino del Sol Santa Fe", direccion: "Edgar Pacheco" },
  { agIds: ["ag5457_5e20f7d1c1032"], agencia: "Honda Metepec", grupo: "Sunroad", razonSocial: "Camino del Sol Santa Fe", direccion: "Edgar Pacheco" },
  { agIds: ["ag0532_5598bb1c488c9"], agencia: "Honda Santa Fe", grupo: "Sunroad", razonSocial: "Camino del Sol Baja Norte", direccion: "Edgar Pacheco" },
];

// Llave para comparar nombres sin acentos, mayúsculas ni espacios extra.
const llaveTexto = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const MATRIZ_POR_AG_ID = {};
const MATRIZ_POR_NOMBRE = {};
AGENCIAS_MATRIZ.forEach((fila) => {
  fila.agIds.forEach((agId) => {
    MATRIZ_POR_AG_ID[agId] = fila;
  });
  MATRIZ_POR_NOMBRE[llaveTexto(fila.agencia)] = fila;
});

const buscarAgenciaEnMatriz = (agId, agenciaNombre) => {
  const porId = MATRIZ_POR_AG_ID[String(agId || "").trim()];
  if (porId) return porId;
  return MATRIZ_POR_NOMBRE[llaveTexto(agenciaNombre)] || null;
};

// ---------------------------------------------------------------------------
// Afluencia (Seekop)
// ---------------------------------------------------------------------------
// Mientras llega el servicio de Seekop, la afluencia se toma de lo que ya
// tenemos: cada cliente registrado en Babilonia cuenta como una visita
// (tenga o no seguimiento). Las "solicitudes" son los clientes que ya tienen
// seguimiento (status asignado).
//
// CUANDO LLEGUE EL SERVICIO DE SEEKOP:
//   1. Cambiar FUENTE_AFLUENCIA a "seekop".
//   2. Implementar cargarAfluenciaSeekop() para que regrese un arreglo de
//      { ag_id, agencia, fecha: "YYYY-MM-DD", cantidad }.
// El resto del dashboard (tarjeta y gráfica) no necesita cambios.
const FUENTE_AFLUENCIA = "babilonia"; // "babilonia" | "seekop"

// eslint-disable-next-line no-unused-vars
const cargarAfluenciaSeekop = async ({ startDate, endDate, agencias }) => {
  // TODO: conectar con el servicio de Seekop cuando lo compartan.
  return [];
};

// ---------------------------------------------------------------------------
// Tooltip de información (ícono "i")
// ---------------------------------------------------------------------------
// Se renderiza en un portal con position: fixed para que no lo recorten las
// tarjetas (StatCard usa overflow-hidden). Funciona con hover, foco (teclado)
// y clic/tap (móvil).
function InfoTooltip({ text, size = 13 }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    width: 300,
    placement: "top",
  });
  const iconRef = useRef(null);

  const isDark =
    typeof document !== "undefined" &&
    document.body.classList.contains("theme-default");

  const show = () => {
    const el = iconRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const width = Math.min(310, window.innerWidth - 16);
    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    const placement = rect.top > 180 ? "top" : "bottom";
    const top = placement === "top" ? rect.top - 8 : rect.bottom + 8;

    setPos({ top, left, width, placement });
    setOpen(true);
  };

  const hide = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;

    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);

    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (!text) return null;

  return (
    <>
      <span
        ref={iconRef}
        role="button"
        tabIndex={0}
        aria-label="Cómo se calcula"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(event) => {
          event.stopPropagation();
          if (open) hide();
          else show();
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: "6px",
          cursor: "help",
          opacity: 0.65,
          verticalAlign: "middle",
          flexShrink: 0,
        }}
      >
        <i className="fa-solid fa-circle-info" style={{ fontSize: size }}></i>
      </span>

      {open &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              transform: pos.placement === "top" ? "translateY(-100%)" : "none",
              zIndex: 3000,
              background: isDark ? "#f9fafb" : "#111827",
              color: isDark ? "#111827" : "#f9fafb",
              fontSize: "12.5px",
              fontWeight: 400,
              lineHeight: 1.45,
              textAlign: "left",
              padding: "10px 12px",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              whiteSpace: "pre-line",
              pointerEvents: "none",
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

// Encabezado de tarjeta de gráfica con su tooltip de "cómo se calcula".
function ChartHeader({ title, info }) {
  return (
    <CardHeader>
      <span className="d-inline-flex align-items-center">
        {title}
        <InfoTooltip text={info} />
      </span>
    </CardHeader>
  );
}

// Explicación de cada indicador, basada en la lógica de este archivo.
// Si cambia el cálculo de algún indicador, actualizar aquí también.
const INFO_INDICADORES = {
  solicitudesFinanciera:
    "Cuántas solicitudes pasaron por cada financiera y cómo terminaron.\n• Cambiadas: se cambió a otra financiera, así que se cuenta como rechazada por esa financiera.\n• La financiera actual (la última elegida) se separa por su status: Autorizado, Rechazado o Pendiente de buró.\n• Primera opción: con qué financiera se empezó. En registros anteriores se deduce de las financieras marcadas; si no se puede saber, no se cuenta.\nNo incluye ventas de contado.",
  totalClientes:
    "Todos los clientes que se registraron en el periodo seleccionado, ya sea que tengan seguimiento o no.",
  conStatus:
    'Clientes que ya tienen un avance registrado (por ejemplo: en revisión, autorizado o cotizado).\n"Faltan por asignar" son los que todavía no tienen seguimiento.\nAbajo ves cuántos son de crédito, contado y arrendamiento.',
  pctAprobacion:
    "Tasa de aprobación = Aprobados / (Universo - Pendientes de buró).\nUniverso: solicitudes de crédito con status. No se cuentan las que siguen pendientes de buró porque todavía no tienen resultado.\nSolo considera crédito.",
  pctRechazo:
    "Tasa de rechazo = Rechazos / (Universo - Pendientes de buró).\nUniverso: solicitudes de crédito con status. No se cuentan las que siguen pendientes de buró porque todavía no tienen resultado.\nSolo considera crédito.",
  formalizadas:
    "Ventas que se cerraron (formalizaron) dentro del periodo, tomando en cuenta el día en que se cerró la venta.",
  statusCredito:
    "En qué etapa van las solicitudes de crédito: autorizadas, rechazadas o pendientes de buró, y el detalle de cada una.",
  statusContado:
    "En qué etapa van las ventas de contado: en cotización o ya formalizadas.",
  statusArrendamiento:
    "En qué etapa van los arrendamientos: cotización, en espera de autorización, autorizado o rechazado, y formalizado.",
  fuente:
    'De dónde llegaron los clientes (por ejemplo: digital, cartera, piso).\nLas fuentes con menos clientes se juntan en "Otros".',
  financiera:
    'Con qué financiera se tramitó cada solicitud.\nLas financieras con menos solicitudes se juntan en "Otros".',
  tipoPago:
    "Cuántos clientes compran a crédito, de contado o con arrendamiento.",
  ventaBabilonia: "Cuántas ventas se hicieron en Babilonia y cuántas no.",
  solicitudesTardias:
    "Solicitudes que se capturaron el día 1 pero que eran del mes anterior.\nSe cuentan en el mes al que pertenecen, no en el día en que se capturaron.\nCada color es el mes al que pertenecen. Da clic en una barra para ver solo esas solicitudes.",
  ventaNr:
    "Cuántas ventas se cotizaron con el sistema de NR (CrediNissan) y cuántas con el proceso normal de Babilonia.",
  pruebaManejo: "Cuántos clientes hicieron prueba de manejo y cuántos no.",
  planes:
    "Qué planes (Elite, Gold, Platinum, Plan cliente) eligen más los clientes.",
  topAccesorios: "Los 5 accesorios o garantías que más se venden.",
  ganancias:
    "Cuánto se ganó en accesorios y garantías en el periodo comparado con los mismos días del mes anterior.\nSi eliges fechas (ej. 01/10 – 07/10) se compara contra 01/09 – 07/09. Con el rango por default compara el mes en curso contra los mismos días del mes anterior.",
  agenciaDepartamento:
    "Cuántas solicitudes tiene cada agencia, separadas en autos nuevos y seminuevos.",
  acumulado:
    "Cómo van las solicitudes acumuladas día a día en el periodo comparado con los mismos días del mes anterior.\nSi eliges fechas (ej. 01/10 – 07/10) se compara contra 01/09 – 07/09. Con el rango por default compara el mes en curso contra los mismos días del mes anterior.",
  statusSimple:
    "Cuántas solicitudes de crédito hay en cada etapa: autorizadas, rechazadas y pendientes de buró.",
  substatus:
    "El detalle de en qué punto va cada solicitud de crédito (en análisis, incompleta, condicionada, formalizada, etc.).",
  funnel:
    "El camino del cliente: desde que se registra (con o sin seguimiento), a que se le da seguimiento, a autorizada o cotizada, hasta la venta cerrada.\nEn cada paso ves cuántos avanzan del paso anterior. Da clic en un paso para filtrar todo el tablero con esa etapa.",
  afluencia:
    "Compara cuántas personas visitaron la agencia contra cuántas se convirtieron en solicitud con seguimiento.\nPor ahora la afluencia se toma de los clientes registrados en Babilonia; cuando esté lista la conexión con Seekop se tomará de ahí.",
  historicoMensual:
    "Cuántas solicitudes entraron cada mes del año, cuántas tuvieron seguimiento y cuántas se cerraron.\nDa clic en un mes para ver solo ese mes en todo el tablero.",
  aprobacionMensual:
    "Porcentaje de solicitudes de crédito autorizadas en cada mes del año.\nSolo considera crédito. Da clic en un mes para ver solo ese mes en todo el tablero.",
  razonSocial:
    "Resultados por razón social: solicitudes, ventas cerradas y porcentaje de aprobación de crédito.",
  tendencia: "Cuántas solicitudes entran cada día.",
  topAgencias: "Las 10 agencias con más solicitudes.",
  topAgenciasFormalizacion: "Las 10 agencias que más ventas han cerrado.",
  conversionAgencia:
    "Por agencia: cuántas solicitudes se autorizaron y cuántas terminaron en venta cerrada.",
  asesor:
    "Resultados de cada asesor: cuántas solicitudes tiene, cuántas se autorizaron, rechazaron o cerraron, y cuánto aporta del total.\n\"Tardías\" son las que se capturaron el día 1 pero eran del mes anterior.\nDa clic en un asesor para ver a sus clientes.",
  formalizacionesFinanciera: "Con qué financieras se cierran más ventas.",
  direccion:
    "Resultados por dirección: solicitudes, ventas cerradas y porcentaje de aprobación de crédito.\nDa clic en una dirección para filtrar el tablero.",
  rankingPaquetes:
    "Qué asesores venden más planes (Elite, Gold, Platinum, Plan cliente).",
  rankingAccesorios: "Qué asesores venden más accesorios y garantías.",
};

// ---------------------------------------------------------------------------
// Íconos en SVG (no dependen de la versión de Font Awesome del tema)
// ---------------------------------------------------------------------------
const IconoX = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const IconoFlechaIzq = ({ size = 13 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M15 5l-7 7 7 7"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const IconoPersonas = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <circle cx="9" cy="8" r="3.5" fill="currentColor" />
    <circle cx="17" cy="9" r="2.8" fill="currentColor" opacity="0.75" />
    <path d="M2 20c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5z" fill="currentColor" />
    <path
      d="M15.5 13.6c3.3.1 6.5 2.2 6.5 6.4h-4.6c0-2.6-.7-4.7-1.9-6.4z"
      fill="currentColor"
      opacity="0.75"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// Embudo de solicitudes (HTML/CSS)
// ---------------------------------------------------------------------------
// Antes era un funnel de Highcharts con etiquetas por fuera, que se encimaban
// o se cortaban en pantallas chicas. Ahora cada etapa es una barra con el
// texto adentro que se acomoda solo (responsivo) y se puede dar clic.
function EmbudoSolicitudes({
  stages = [],
  footer = "",
  textColor,
  mutedColor,
  onStageClick,
}) {
  if (!stages.length || !stages[0].y) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: 300, color: mutedColor, fontWeight: 600 }}
      >
        Sin datos para mostrar
      </div>
    );
  }

  // Anchos fijos que van cerrando el embudo (no proporcionales al valor
  // para que el texto siempre quepa; el número dice la cantidad real).
  const anchoMin = 46;
  const paso = stages.length > 1 ? (100 - anchoMin) / (stages.length - 1) : 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        padding: "4px 0",
        containerType: "inline-size",
      }}
    >
      {stages.map((stage, index) => {
        const ancho = 100 - paso * index;
        const anchoSiguiente = Math.max(ancho - paso, anchoMin - 6);
        // Recorte del trapecio en % del propio ancho de la barra.
        const inset = ((ancho - anchoSiguiente) / 2 / ancho) * 100;

        return (
          <button
            key={stage.key}
            type="button"
            onClick={() => onStageClick && onStageClick(stage)}
            title={stage.description}
            style={{
              width: `${ancho}%`,
              minHeight: 74,
              border: "none",
              background: stage.color,
              color: "#ffffff",
              clipPath: `polygon(0 0, 100% 0, ${100 - inset}% 100%, ${inset}% 100%)`,
              padding: `8px ${Math.max(inset, 4) + 2}%`,
              cursor: onStageClick ? "pointer" : "default",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              lineHeight: 1.15,
              transition: "filter .15s ease, transform .15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = "brightness(1.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = "none";
            }}
          >
            <span
              style={{
                fontSize: "clamp(10px, 3.4cqw, 13px)",
                fontWeight: 600,
                opacity: 0.95,
                wordBreak: "break-word",
              }}
            >
              {stage.name}
            </span>
            <span
              style={{
                fontSize: "clamp(16px, 6cqw, 24px)",
                fontWeight: 800,
                margin: "2px 0",
              }}
            >
              {new Intl.NumberFormat("es-MX").format(stage.y)}
            </span>
            <span
              style={{
                fontSize: "clamp(9px, 3cqw, 11.5px)",
                fontWeight: 600,
                opacity: 0.92,
              }}
            >
              {index === 0
                ? "100% del total"
                : `${stage.pctPrevious}% del paso anterior · ${stage.pctTotal}% del total`}
            </span>
          </button>
        );
      })}

      {footer ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            fontWeight: 600,
            color: mutedColor,
            textAlign: "center",
          }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel lateral: desglose por asesor y detalle de clientes
// ---------------------------------------------------------------------------
function PanelDesglose({
  open,
  onClose,
  titulo,
  chips = [],
  asesores = [],
  asesorSeleccionado,
  onSelectAsesor,
  clientes = [],
  isDarkMode,
  textColor,
  mutedColor,
  gridColor,
  colorSet,
}) {
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    setBusqueda("");
  }, [asesorSeleccionado]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const bg = isDarkMode ? "#111827" : "#ffffff";
  const softBg = isDarkMode ? "#1f2937" : "#f4f6f8";

  const badgeColor = (texto) => {
    const t = llaveTexto(texto);
    if (t.includes("formalizado")) return colorSet.turquesa;
    if (t.includes("autorizado")) return colorSet.turquesa;
    if (t.includes("rechazado")) return colorSet.rojo;
    if (t.includes("buro") || t.includes("pendiente")) return colorSet.azul;
    if (t.includes("cotizacion")) return colorSet.naranja;
    return colorSet.gris;
  };

  const clientesFiltrados = busqueda.trim()
    ? clientes.filter((c) =>
        llaveTexto(
          `${c.nombreCompleto} ${c.telefono} ${c.correo} ${c.folio} ${c.vehiculo} ${c.esTardia ? "tardia tardía" : ""}`,
        ).includes(llaveTexto(busqueda)),
      )
    : clientes;

  return createPortal(
    <div
      role="dialog"
      aria-label={titulo}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: "min(580px, 100vw)",
        background: bg,
        color: textColor,
        zIndex: 2000,
        boxShadow: "-12px 0 32px rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
        borderLeft: `1px solid ${gridColor}`,
      }}
    >
      <div
        style={{
          padding: "16px 18px 12px",
          borderBottom: `1px solid ${gridColor}`,
        }}
      >
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            {asesorSeleccionado ? (
              <button
                type="button"
                onClick={() => onSelectAsesor(null)}
                style={{
                  border: `1px solid ${gridColor}`,
                  background: softBg,
                  color: textColor,
                  borderRadius: 999,
                  padding: "3px 12px 3px 8px",
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <span className="d-inline-flex align-items-center gap-1">
                  <IconoFlechaIzq />
                  Regresar a asesores
                </span>
              </button>
            ) : null}
            <div style={{ fontSize: 17, fontWeight: 800 }}>
              {asesorSeleccionado
                ? `Clientes de ${asesorSeleccionado}`
                : titulo}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar (Esc)"
            aria-label="Cerrar"
            style={{
              flexShrink: 0,
              width: 34,
              height: 34,
              borderRadius: 10,
              border: `1px solid ${gridColor}`,
              background: softBg,
              color: textColor,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <IconoX size={16} />
          </button>
        </div>

        {chips.length ? (
          <div className="d-flex flex-wrap gap-1 mt-2">
            {chips.map((chip) => (
              <span
                key={chip}
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  background: softBg,
                  borderRadius: 999,
                  padding: "3px 10px",
                  color: mutedColor,
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ overflowY: "auto", flex: 1, padding: "12px 18px 24px" }}>
        {!asesorSeleccionado ? (
          asesores.length ? (
            <>
              <div style={{ fontSize: 12, color: mutedColor, marginBottom: 8 }}>
                Da clic en un asesor para ver a sus clientes.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead>
                    <tr>
                      <th className="black-text">Asesor</th>
                      <th className="black-text text-end">Clientes</th>
                      <th className="black-text text-end">Con seguimiento</th>
                      <th className="black-text text-end">Autorizadas</th>
                      <th className="black-text text-end">Formalizadas</th>
                      <th className="black-text text-end">% Aprob.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asesores.map((row) => (
                      <tr
                        key={row.asesor}
                        onClick={() => onSelectAsesor(row.asesor)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <div style={{ fontWeight: 700 }}>{row.asesor}</div>
                          <div style={{ fontSize: 11, color: mutedColor }}>
                            {row.agencias}
                          </div>
                        </td>
                        <td className="text-end">{row.total}</td>
                        <td className="text-end">{row.conStatus}</td>
                        <td className="text-end">{row.autorizadas}</td>
                        <td className="text-end">{row.formalizadas}</td>
                        <td className="text-end">
                          {row.creditos ? `${row.pctAprobacion}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center" style={{ color: mutedColor }}>
              Sin datos para esta selección
            </div>
          )
        ) : (
          <>
            <input
              type="search"
              className="form-control form-control-sm mb-3"
              placeholder="Buscar por nombre, teléfono, correo, folio o auto…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <div style={{ fontSize: 12, color: mutedColor, marginBottom: 8 }}>
              {clientesFiltrados.length} cliente(s)
            </div>
            {clientesFiltrados.map((c) => (
              <div
                key={c.id}
                style={{
                  background: softBg,
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div style={{ fontWeight: 700, minWidth: 0 }}>
                    {c.nombreCompleto}
                    {c.esTardia ? (
                      <span
                        title={
                          c.fechaCapturaTardia
                            ? `Se capturó el ${c.fechaCapturaTardia}, pero cuenta para el ${c.fecha}`
                            : "Se capturó después del cierre de mes"
                        }
                        style={{
                          display: "inline-block",
                          marginLeft: 6,
                          background: "#f2994a",
                          color: "#fff",
                          fontSize: 10,
                          fontWeight: 700,
                          borderRadius: 999,
                          padding: "1px 8px",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <i className="fa-solid fa-clock me-1"></i>
                        Solicitud tardía
                      </span>
                    ) : null}
                  </div>
                  <span
                    style={{
                      background: badgeColor(c.statusTexto),
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: "2px 9px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.statusTexto}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: mutedColor,
                    marginTop: 4,
                    lineHeight: 1.5,
                  }}
                >
                  <div>
                    {c.fecha} · {c.agencia} · {c.tipoPago}
                    {c.financiera ? ` · ${c.financiera}` : ""}
                  </div>
                  {c.vehiculo ? <div>{c.vehiculo}</div> : null}
                  {c.esTardia ? (
                    <div style={{ color: "#d9822b", fontWeight: 600 }}>
                      Capturada tarde
                      {c.fechaCapturaTardia
                        ? ` el ${c.fechaCapturaTardia}`
                        : ""}
                      ; cuenta para el {c.fecha}
                    </div>
                  ) : null}
                  <div>
                    {c.telefono ? (
                      <span className="me-2">
                        <i className="fa-solid fa-phone me-1"></i>
                        {c.telefono}
                      </span>
                    ) : null}
                    {c.correo ? (
                      <span>
                        <i className="fa-solid fa-envelope me-1"></i>
                        {c.correo}
                      </span>
                    ) : null}
                  </div>
                  <div>
                    Fuente: {c.fuente} · Prueba de manejo: {c.pruebaManejo}
                    {c.folio ? ` · Folio: ${c.folio}` : ""}
                  </div>
                  {c.planes.length ? (
                    <div>
                      <b>Planes:</b> {c.planes.join(", ")}
                    </div>
                  ) : null}
                  {c.accesorios.length ? (
                    <div>
                      <b>Accesorios:</b>{" "}
                      {c.accesorios
                        .map((a) => (a.monto ? `${a.titulo} (${a.monto})` : a.titulo))
                        .join(", ")}
                    </div>
                  ) : null}
                  {c.garantias.length ? (
                    <div>
                      <b>Garantías:</b>{" "}
                      {c.garantias
                        .map((a) => (a.monto ? `${a.titulo} (${a.monto})` : a.titulo))
                        .join(", ")}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {!clientesFiltrados.length ? (
              <div className="text-center" style={{ color: mutedColor }}>
                Sin clientes
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function DashboardRendimiento() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(() => ({
    // Por default: mes actual (antes arrancaba vacío = toda la tabla)
    ...getRangoDefault(),
    grupo: "Todas",
    direccion: "Todas",
    razonSocial: "Todas",
    agencia: [], // multi-selección: arreglo vacío significa "Todas"
    departamento: "Todas",
    tipoPago: "Todas",
  }));

  // Selecciones hechas con clic en las gráficas que NO tienen un filtro
  // propio arriba (status, fuente, financiera, plan, etc.). Las que sí tienen
  // filtro (agencia, dirección, departamento, tipo de pago...) mueven
  // directamente ese filtro. Formato: { [dimension]: { value, label } }
  const [seleccionGraficas, setSeleccionGraficas] = useState({});

  // Panel lateral de desglose por asesor / clientes
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [asesorPanel, setAsesorPanel] = useState(null);

  // Histórico anual: NO se consulta al abrir el dashboard (es la consulta más
  // pesada). Se carga solo cuando el usuario da clic en "Cargar histórico".
  const [historicoSolicitado, setHistoricoSolicitado] = useState(false);
  const [anioHistorico, setAnioHistorico] = useState(() => Moment().year());
  const [rawDataAnual, setRawDataAnual] = useState([]);
  const [loadingAnual, setLoadingAnual] = useState(false);

  // Afluencia del servicio de Seekop (vacío mientras FUENTE_AFLUENCIA sea
  // "babilonia")
  const [afluenciaSeekop, setAfluenciaSeekop] = useState([]);

  const datePickerRef = useRef(null);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const [agenciaDropdownOpen, setAgenciaDropdownOpen] = useState(false);
  const agenciaDropdownRef = useRef(null);

  const accessToken =
    "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";
  const apiUrl = "https://api.checkmark.mx/solicitudes/consultar";

  const isDarkMode = document.body.classList.contains("theme-default");
  const textColor = isDarkMode ? "#e5e7eb" : "#111827";
  const mutedColor = isDarkMode ? "#9ca3af" : "#6b7280";
  const softTextColor = isDarkMode ? "#cbd5e1" : "#4b5563";
  const gridColor = isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const chartBg = "transparent";
  const cardBg = isDarkMode ? "#111827" : "#f8f8f8";

  const colorSet = {
    azul: "#2f80ed",
    azulOscuro: "#1f3c88",
    naranja: "#f2994a",
    rojo: "#eb5757",
    morado: "#9b51e0",
    turquesa: "#27ae60",
    amarillo: "#f2c94c",
    gris: "#bdbdbd",
    rosa: "#e056fd",
    celeste: "#56ccf2",
  };

  const STATUS_ORDER = ["Autorizado", "Rechazado", "Pendiente de Buro"];

  const CONTADO_STATUS_ORDER = ["Cotización", "Formalizado"];

  // Flujo de arrendamiento: se cotiza, se manda a autorización de la
  // arrendadora (igual que un crédito se manda a la financiera), se
  // autoriza o rechaza, y finalmente se formaliza el contrato.
  const ARRENDAMIENTO_STATUS_ORDER = [
    "Cotización",
    "Pendiente de Autorización",
    "Autorizado",
    "Rechazado",
    "Formalizado",
  ];

  const SUBSTATUS_ORDER = [
    "Sin substatus",
    "Analisis",
    "Incompleto",
    "Condicionado Viable",
    "Condicionado No Viable",
    "Facturado",
    "Formalizado",
    "Aprobado Sin Formalizar",
    "Rechazado",
  ];

  const PLANES_ORDER = ["Elite", "Gold", "Platinum", "Plan cliente"];

  const PLANES_RANKING_ORDER = ["Elite", "Gold", "Platinum", "Plan cliente"];

  const ACCESORIO_GARANTIA_TIPOS_ORDER = ["accesorios", "garantias"];

  const pick = (...values) => {
    for (const value of values) {
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return value;
      }
    }
    return "";
  };

  const normalizeText = (value) => String(value || "").trim();

  const safeJsonParse = (value, fallback = null) => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  };

  const normalizeStatus = (value) => {
    const raw = normalizeText(value);
    const v = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!v) return "Sin status";
    if (v === "sin status") return "Sin status";
    if (v === "autorizado") return "Autorizado";
    if (v === "rechazado") return "Rechazado";
    if (v === "pendiente de buro") return "Pendiente de Buro";
    if (v === "pendiente de autorizacion") return "Pendiente de Autorización";
    if (v === "cotizacion") return "Cotización";
    if (v === "formalizado") return "Formalizado";

    return "Sin status";
  };

  const normalizeSubStatus = (value) => {
    const raw = normalizeText(value);
    const v = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (!v) return "Sin substatus";
    if (v === "sin substatus") return "Sin substatus";
    if (v === "analisis") return "Analisis";
    if (v === "aprobado sin formalizar") return "Aprobado Sin Formalizar";
    if (v === "aprobado sin finalizar") return "Aprobado Sin Formalizar";
    if (v === "condicionado viable") return "Condicionado Viable";
    if (v === "condicionado no viable") return "Condicionado No Viable";
    if (v === "facturado") return "Facturado";
    if (v === "formalizado") return "Formalizado";
    if (v === "incompleto") return "Incompleto";
    if (v === "rechazado") return "Rechazado";

    return "Sin substatus";
  };

  const normalizeFuente = (value) => normalizeText(value) || "Digital";
  const normalizeFinanciera = (value) => normalizeText(value) || "(En blanco)";
  const normalizeAgencia = (value) => normalizeText(value) || "(En blanco)";
  const normalizeDireccion = (value) => normalizeText(value) || "(En blanco)";
  const normalizeRazonSocial = (value) => normalizeText(value) || "(En blanco)";
  const normalizeAsesor = (value) => normalizeText(value) || "EXTERNO";

  const normalizeTipoPago = (value) => {
    const raw = normalizeText(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (raw === "si") return "Contado";
    if (raw === "no") return "Crédito";
    if (raw === "arrendamiento") return "Arrendamiento";

    return "Crédito";
  };

  const normalizeDepartamento = (value, item = null) => {
    const raw = normalizeText(value || "");
    const v = raw.toLowerCase();

    if (v.includes("semi")) return "Seminuevos";
    if (v.includes("nuevo")) return "Nuevos";

    const tipoAuto = normalizeText(
      pick(item?.tipo_auto, item?.tipo, item?.departamento),
    ).toLowerCase();

    if (tipoAuto.includes("semi")) return "Seminuevos";
    if (tipoAuto.includes("nuevo")) return "Nuevos";

    return "(En blanco)";
  };

  const normalizePlanTitle = (value) => {
    const raw = normalizeText(value);
    const v = raw.toLowerCase();

    if (!v) return "Sin plan";
    if (v === "elite") return "Elite";
    if (v === "gold") return "Gold";
    if (v === "platinum") return "Platinum";
    if (v === "plan cliente") return "Plan cliente";

    return raw;
  };

  const normalizeAccessoryTitle = (value) => {
    return normalizeText(value) || "Sin nombre";
  };

  const parsePlanesFinanciamiento = (value) => {
    const parsed = safeJsonParse(value, []);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((plan) => ({
      ...plan,
      title: normalizePlanTitle(plan?.title),
      isSelected: Boolean(plan?.isSelected),
      accessories: Array.isArray(plan?.accessories)
        ? plan.accessories.map((item) => ({
            ...item,
            title: normalizeAccessoryTitle(item?.title),
            type: normalizeText(item?.type)?.toLowerCase() || "sin tipo",
          }))
        : [],
    }));
  };

  const parseDate = (item) => {
    const raw = pick(
      item?.fecha,
      item?.fecha_registro,
      item?.created_at,
      item?.fecha_creacion,
      item?.createdAt,
    );

    if (!raw) return null;

    const m = Moment(
      raw,
      [
        "YYYY-MM-DD",
        "YYYY-MM-DD HH:mm:ss",
        "DD/MM/YYYY",
        "DD/MM/YYYY HH:mm:ss",
        "MM/DD/YYYY",
        "MM/DD/YYYY HH:mm:ss",
        Moment.ISO_8601,
      ],
      true,
    );

    if (m.isValid()) return m;

    const m2 = Moment(raw);
    return m2.isValid() ? m2 : null;
  };

  // Fecha en la que una solicitud pasó a "Formalizado", tomada del historial
  // de cambios (historial_cambios_status). Se usa para que, al filtrar por
  // fecha, una solicitud formalizada cuente en el rango en que se formalizó
  // y no en el que se registró originalmente.
  const parseFechaFormalizacion = (item) => {
    const historial = safeJsonParse(
      pick(item?.historial_cambios_status, item?.historialCambiosStatus),
      [],
    );

    if (!Array.isArray(historial) || !historial.length) return null;

    const cambiosAFormalizado = historial.filter((cambio) => {
      const valorNuevo = normalizeText(cambio?.valor_nuevo)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return valorNuevo === "formalizado";
    });

    if (!cambiosAFormalizado.length) return null;

    const fechas = cambiosAFormalizado
      .map((cambio) =>
        Moment(
          cambio?.fecha,
          ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", Moment.ISO_8601],
          true,
        ),
      )
      .filter((m) => m.isValid());

    if (!fechas.length) return null;

    // Si hubo más de un cambio a "Formalizado" (ej. se revirtió y se volvió
    // a formalizar), se usa el más reciente.
    return fechas.reduce((latest, current) =>
      current.isAfter(latest) ? current : latest,
    );
  };

  const normalizePruebaManejo = (value) => {
    const parsed = safeJsonParse(value, null);
    const raw = normalizeText(
      parsed?.realizo_prueba_manejo ??
        parsed?.realizoPruebaManejo ??
        parsed?.realizo ??
        value,
    );

    const normalized = raw
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (["si", "sí", "yes", "true", "1"].includes(normalized)) {
      return "Sí";
    }

    return "No";
  };

  // venta_babilonia solo maneja "Si" / "No". Si llegara vacío (registros
  // viejos), se cuenta aparte como "Sin registrar" para no inflar el "No".
  const normalizeVentaBabilonia = (value) => {
    const v = normalizeText(value)
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (v === "si") return "Sí";
    if (v === "no") return "No";
    return "Sin registrar";
  };

  // Una venta se hizo con el flujo de NR (Procotiza) si tiene ID de
  // cotización de Procotiza guardado.
  const esVentaNrRow = (item) => {
    const idCotizacion = normalizeText(item?.procotiza_id_cotizacion);
    return idCotizacion !== "" && idCotizacion !== "0";
  };

  // Solicitud tardía: se capturó el día de habilitación pero corresponde al
  // mes anterior. Su fecha_creacion ya es la del mes al que corresponde, así
  // que el resto del tablero no cambia; aquí solo se identifica.
  const esSolicitudTardiaRow = (item) => {
    const v = normalizeText(item?.solicitud_tardia).toLowerCase();
    return v === "1" || v === "si" || v === "true";
  };

  const normalizeRow = (item) => {
    const planesFinanciamiento = parsePlanesFinanciamiento(
      pick(item?.planes_financiamiento, item?.planesFinanciamiento),
    );

    const selectedPlans = planesFinanciamiento.filter(
      (plan) => plan.isSelected,
    );

    const selectedAccessories = selectedPlans.flatMap((plan) =>
      Array.isArray(plan.accessories) ? plan.accessories : [],
    );

    const matriz = buscarAgenciaEnMatriz(
      item?.ag_id,
      pick(item?.agencia, item?.agency),
    );

    return {
      ...item,
      _status: normalizeStatus(item?.status),
      _subStatus: normalizeSubStatus(item?.sub_status),
      _tipoPago: normalizeTipoPago(
        pick(item?.depende_auto, item?.depente_auto),
      ),
      _fuente: normalizeFuente(
        pick(item?.fuente, item?.origen_lead, item?.lead_source),
      ),
      _financiera: normalizeFinanciera(
        pick(item?.financiera, item?.financiera_nombre),
      ),
      _agencia: normalizeAgencia(pick(item?.agencia, item?.agency)),
      _departamento: normalizeDepartamento(
        pick(item?.departamento, item?.tipo_auto),
        item,
      ),
      // Si algún día la API los manda, se respetan; si no, salen de la matriz.
      _grupo: normalizeText(pick(item?.grupo, matriz?.grupo)) || SIN_ASIGNAR,
      _direccion: normalizeDireccion(
        pick(item?.direccion, item?.director, matriz?.direccion, SIN_ASIGNAR),
      ),
      _razonSocial: normalizeRazonSocial(
        pick(
          item?.razon_social,
          item?.razonSocial,
          item?.empresa,
          matriz?.razonSocial,
          SIN_ASIGNAR,
        ),
      ),
      _asesor: normalizeAsesor(
        pick(item?.asesor, item?.nombre_asesor, item?.asesor_nombre),
      ),
      _fecha: parseDate(item),
      _fechaFormalizacion: parseFechaFormalizacion(item),
      _ventaBabilonia: normalizeVentaBabilonia(item?.venta_babilonia),
      _esVentaNr: esVentaNrRow(item),
      _esTardia: esSolicitudTardiaRow(item),
      _realizoPruebaManejo: normalizePruebaManejo(
        pick(item?.prueba_manejo, item?.pruebaManejo),
      ),
      _planesFinanciamiento: planesFinanciamiento,
      _selectedPlanTitles: selectedPlans.map((plan) => plan.title),
      _selectedAccessories: selectedAccessories,
    };
  };

  // Evita que una respuesta vieja (de un rango anterior) pise a la nueva si
  // el usuario cambia de rango rápido.
  const fetchRequestIdRef = useRef(0);

  const fetchData = async (startDate, endDate) => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);

    try {
      const agenciasStorage =
        JSON.parse(localStorage.getItem("agencias")) || [];
      const modulos = JSON.parse(localStorage.getItem("modulos")) || [];
      const userId = localStorage.getItem("userId");

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          modulos,
          usuario_id: userId,
          agencias: agenciasStorage,
          // Rango seleccionado (la API filtra por fecha_creacion)
          startDate,
          endDate,
        }),
      });

      const data = await response.json();

      if (requestId !== fetchRequestIdRef.current) return;

      if (data?.statusCode === 200 && Array.isArray(data?.data?.resultados)) {
        const normalized = data.data.resultados.map(normalizeRow);
        setRawData(normalized);
      } else if (data?.statusCode === 404) {
        setRawData([]);
        Swal.fire({
          icon: "warning",
          title: "Sin resultados",
          text: "No se encontraron resultados.",
        });
      } else {
        setRawData([]);
        console.error("Respuesta no esperada:", data);
      }
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      setRawData([]);
      console.error("Error al consultar:", error);
    } finally {
      if (requestId === fetchRequestIdRef.current) setLoading(false);
    }
  };

  // Se vuelve a consultar la API solo cuando cambia el rango de fechas; los
  // demás filtros (agencia, tipo de pago, etc.) se siguen aplicando en el
  // navegador sin volver a pedir datos.
  useEffect(() => {
    fetchData(filters.startDate, filters.endDate);
  }, [filters.startDate, filters.endDate]);

  // Histórico anual: misma API y mismos parámetros (startDate / endDate),
  // solo que con el año completo. Se consulta aparte, únicamente después de
  // que el usuario lo pide con el botón (y de nuevo si cambia el año).
  const fetchAnualRequestIdRef = useRef(0);

  useEffect(() => {
    if (!historicoSolicitado) return;
    const requestId = ++fetchAnualRequestIdRef.current;
    const inicio = Moment().year(anioHistorico).startOf("year");
    const finAnio = Moment().year(anioHistorico).endOf("year");
    const fin = finAnio.isAfter(Moment()) ? Moment() : finAnio;

    const cargar = async () => {
      setLoadingAnual(true);
      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_token: accessToken,
            modulos: JSON.parse(localStorage.getItem("modulos")) || [],
            usuario_id: localStorage.getItem("userId"),
            agencias: JSON.parse(localStorage.getItem("agencias")) || [],
            startDate: inicio.format("YYYY-MM-DD"),
            endDate: fin.format("YYYY-MM-DD"),
          }),
        });
        const data = await response.json();
        if (requestId !== fetchAnualRequestIdRef.current) return;

        if (data?.statusCode === 200 && Array.isArray(data?.data?.resultados)) {
          setRawDataAnual(data.data.resultados.map(normalizeRow));
        } else {
          setRawDataAnual([]);
        }
      } catch (error) {
        if (requestId !== fetchAnualRequestIdRef.current) return;
        setRawDataAnual([]);
        console.error("Error al consultar histórico anual:", error);
      } finally {
        if (requestId === fetchAnualRequestIdRef.current) setLoadingAnual(false);
      }
    };

    cargar();
  }, [anioHistorico, historicoSolicitado]);

  // Afluencia de Seekop (solo cuando ya esté conectado el servicio)
  useEffect(() => {
    if (FUENTE_AFLUENCIA !== "seekop") return undefined;
    let cancelado = false;

    cargarAfluenciaSeekop({
      startDate: filters.startDate,
      endDate: filters.endDate,
      agencias: JSON.parse(localStorage.getItem("agencias")) || [],
    })
      .then((rows) => {
        if (!cancelado) setAfluenciaSeekop(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        if (!cancelado) setAfluenciaSeekop([]);
        console.error("Error al consultar afluencia de Seekop:", error);
      });

    return () => {
      cancelado = true;
    };
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const cancelButton = document.querySelector(".cancelBtn");
      const applyButton = document.querySelector(".applyBtn");

      if (cancelButton && cancelButton.textContent !== "Cancelar") {
        cancelButton.textContent = "Cancelar";
      }
      if (applyButton && applyButton.textContent !== "Aplicar") {
        applyButton.textContent = "Aplicar";
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const handleDateApplyEvent = (event, picker) => {
    const startDate = picker.startDate.format("YYYY-MM-DD");
    const endDate = picker.endDate.format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  const resetPickerUi = () => {
    if (datePickerRef.current?.picker) {
      const picker = datePickerRef.current.picker;
      const rangoDefault = getRangoDefault();

      picker.setStartDate(Moment(rangoDefault.startDate, "YYYY-MM-DD"));
      picker.setEndDate(Moment(rangoDefault.endDate, "YYYY-MM-DD"));
      picker.updateView();
      picker.updateCalendars();
      picker.updateElement();
    }

    setDatePickerResetKey((prev) => prev + 1);
  };

  // "Limpiar" regresa al mes actual (ya no a "toda la data").
  const clearDateFilter = () => {
    setFilters((prev) => ({
      ...prev,
      ...getRangoDefault(),
    }));

    resetPickerUi();
  };

  const clearAllFilters = () => {
    setSeleccionGraficas({});
    setFilters({
      ...getRangoDefault(),
      grupo: "Todas",
      direccion: "Todas",
      razonSocial: "Todas",
      agencia: [],
      departamento: "Todas",
      tipoPago: "Todas",
    });

    setAgenciaDropdownOpen(false);
    resetPickerUi();
  };

  const toggleAgenciaFilter = (value) => {
    setFilters((prev) => {
      const current = prev.agencia || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      return { ...prev, agencia: next };
    });
  };

  const isAgenciaAllSelected = filters.agencia.length === 0;

  const rangoDefaultActual = getRangoDefault();
  const isRangoDefault =
    filters.startDate === rangoDefaultActual.startDate &&
    filters.endDate === rangoDefaultActual.endDate;

  const dateRangePresets = {
    "Mes actual": [Moment().startOf("month"), Moment()],
    "Últimos 7 días": [Moment().subtract(6, "days"), Moment()],
    "Mes anterior": [
      Moment().subtract(1, "month").startOf("month"),
      Moment().subtract(1, "month").endOf("month"),
    ],
    "Últimos 90 días": [Moment().subtract(89, "days"), Moment()],
    "Este año": [Moment().startOf("year"), Moment()],
  };

  const agenciaFilterLabel = useMemo(() => {
    if (!filters.agencia.length) return "Todas";
    if (filters.agencia.length === 1) return filters.agencia[0];
    return `${filters.agencia.length} seleccionadas`;
  }, [filters.agencia]);

  useEffect(() => {
    if (!agenciaDropdownOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        agenciaDropdownRef.current &&
        !agenciaDropdownRef.current.contains(event.target)
      ) {
        setAgenciaDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [agenciaDropdownOpen]);

  const isCredito = (item) => item?._tipoPago === "Crédito";
  const isContado = (item) => item?._tipoPago === "Contado";
  const isArrendamiento = (item) => item?._tipoPago === "Arrendamiento";

  const isFormalizada = (item) => {
    return (
      (isCredito(item) && item._subStatus === "Formalizado") ||
      ((isContado(item) || isArrendamiento(item)) &&
        item._status === "Formalizado")
    );
  };

  const hasStatusReal = (item) => {
    return item?._status && item._status !== "Sin status";
  };

  // Autorizada/Rechazada se quedan como indicador específico de Crédito
  // (así se usan en "% De Aprobación" / "% Rechazadas", que son KPIs de
  // crédito). El status "Autorizado"/"Rechazado" propio de Arrendamiento
  // se refleja aparte en la tarjeta "Status de Arrendamiento".
  const isAutorizada = (item) => {
    return isCredito(item) && item._status === "Autorizado";
  };

  const isRechazada = (item) => {
    return (
      isCredito(item) &&
      (item._status === "Rechazado" || item._subStatus === "Rechazado")
    );
  };

  // -----------------------------------------------------------------------
  // Tasas de aprobación / rechazo (crédito)
  // -----------------------------------------------------------------------
  //   Tasa de aprobación = Aprobados / (Universo - Pendientes de buró)
  //   Tasa de rechazo    = Rechazos  / (Universo - Pendientes de buró)
  // Universo = solicitudes de crédito con status. Las pendientes de buró se
  // quitan de la base porque todavía no tienen resultado.
  const isPendienteBuro = (item) =>
    isCredito(item) && item._status === "Pendiente de Buro";

  const baseTasaCredito = (universo, pendientesBuro) =>
    Math.max((universo || 0) - (pendientesBuro || 0), 0);

  const tasaCredito = (valor, universo, pendientesBuro) => {
    const base = baseTasaCredito(universo, pendientesBuro);
    return base ? ((valor / base) * 100).toFixed(1) : "0.0";
  };

  // Etapa "autorizadas / cotizadas" del embudo
  const esAutorizadaOCotizada = (item) => {
    if (!item) return false;
    if (isCredito(item)) return isAutorizada(item) || isFormalizada(item);
    if (isContado(item)) {
      return ["Cotización", "Formalizado"].includes(item._status);
    }
    if (isArrendamiento(item)) {
      return ["Autorizado", "Formalizado"].includes(item._status);
    }
    return false;
  };

  // ---------------------------------------------------------------------
  // Selecciones desde gráficas (sin filtro propio arriba)
  // ---------------------------------------------------------------------
  const cumpleValor = (actual, value) =>
    Array.isArray(value) ? value.includes(actual) : actual === value;

  const cumpleSeleccion = (item, dim, value) => {
    switch (dim) {
      case "fuente":
        return cumpleValor(item._fuente, value);
      case "financiera":
        return cumpleValor(item._financiera, value);
      case "statusCredito":
        return isCredito(item) && item._status === value;
      case "statusSubstatusCredito":
        return (
          isCredito(item) &&
          item._status === value.status &&
          item._subStatus === value.subStatus
        );
      case "substatusCredito":
        return isCredito(item) && item._subStatus === value;
      case "statusContado":
        return isContado(item) && item._status === value;
      case "statusArrendamiento":
        return isArrendamiento(item) && item._status === value;
      case "ventaBabilonia":
        return item._ventaBabilonia === value;
      case "ventaNr":
        return item._esVentaNr === value;
      case "solicitudTardia":
        return item._esTardia === value;
      case "pruebaManejo":
        return value === "Sí"
          ? item._realizoPruebaManejo === "Sí"
          : item._realizoPruebaManejo !== "Sí";
      case "plan":
        return (item._selectedPlanTitles || [])
          .map((plan) => normalizePlanTitle(plan))
          .includes(value);
      case "accesorio":
        return (item._selectedAccessories || []).some(
          (acc) => normalizeAccessoryTitle(acc?.title) === value,
        );
      case "dia":
        return Boolean(item._fecha) && item._fecha.format("YYYY-MM-DD") === value;
      case "resultado":
        if (value === "autorizada") return isAutorizada(item);
        if (value === "rechazada") return isRechazada(item);
        if (value === "formalizada") return isFormalizada(item);
        return true;
      case "etapaEmbudo":
        if (value === "conStatus") return hasStatusReal(item);
        if (value === "autorizadas") return esAutorizadaOCotizada(item);
        if (value === "formalizadas") return isFormalizada(item);
        return true;
      default:
        return true;
    }
  };

  const cumpleSeleccionGraficas = (item) =>
    Object.entries(seleccionGraficas).every(([dim, sel]) =>
      cumpleSeleccion(item, dim, sel.value),
    );

  // ---------------------------------------------------------------------
  // Filtro base compartido
  // ---------------------------------------------------------------------
  // Antes la misma lógica estaba copiada en 3 lugares. Opciones:
  //  - ignorarFecha: no aplica el rango (comparativos e histórico anual)
  //  - excluir: nombre de un filtro a ignorar (para armar las opciones
  //    dinámicas de ese mismo filtro)
  //  - sinSeleccionGraficas: no aplica las selecciones hechas en gráficas
  const pasaFiltros = (item, opciones = {}) => {
    const { ignorarFecha = false, excluir = null, sinSeleccionGraficas = false } =
      opciones;

    if (
      excluir !== "agencia" &&
      filters.agencia.length > 0 &&
      !filters.agencia.includes(item._agencia)
    ) {
      return false;
    }

    if (
      excluir !== "grupo" &&
      filters.grupo !== "Todas" &&
      item._grupo !== filters.grupo
    ) {
      return false;
    }

    if (
      excluir !== "direccion" &&
      filters.direccion !== "Todas" &&
      item._direccion !== filters.direccion
    ) {
      return false;
    }

    if (
      excluir !== "departamento" &&
      filters.departamento !== "Todas" &&
      item._departamento !== filters.departamento
    ) {
      return false;
    }

    if (
      excluir !== "razonSocial" &&
      filters.razonSocial !== "Todas" &&
      item._razonSocial !== filters.razonSocial
    ) {
      return false;
    }

    if (
      excluir !== "tipoPago" &&
      filters.tipoPago !== "Todas" &&
      item._tipoPago !== filters.tipoPago
    ) {
      return false;
    }

    if (!sinSeleccionGraficas && !cumpleSeleccionGraficas(item)) {
      return false;
    }

    if (!ignorarFecha && filters.startDate && filters.endDate) {
      if (!item._fecha || !item._fecha.isValid()) return false;

      const start = Moment(filters.startDate, "YYYY-MM-DD", true).startOf(
        "day",
      );
      const end = Moment(filters.endDate, "YYYY-MM-DD", true).endOf("day");

      if (
        start.isValid() &&
        end.isValid() &&
        !item._fecha.isBetween(start, end, undefined, "[]")
      ) {
        return false;
      }
    }

    return true;
  };

  // Opciones dinámicas: cada filtro solo muestra los valores que existen
  // con los demás filtros aplicados (ej. al elegir una dirección, Razón
  // Social y Agencia solo muestran las de esa dirección). Si el valor
  // elegido ya no aparece, se conserva en la lista para que no "desaparezca".
  const selectOptions = useMemo(() => {
    const valoresDe = (key, filtro) =>
      Array.from(
        new Set(
          rawData
            .filter((item) => pasaFiltros(item, { excluir: filtro }))
            .map((item) => item[key])
            .filter(Boolean),
        ),
      ).sort((a, b) => String(a).localeCompare(String(b)));

    const conTodas = (key, filtro) => {
      const valores = valoresDe(key, filtro);
      const actual = filters[filtro];
      if (actual && actual !== "Todas" && !valores.includes(actual)) {
        valores.push(actual);
      }
      return ["Todas", ...valores];
    };

    const agencias = valoresDe("_agencia", "agencia");
    filters.agencia.forEach((ag) => {
      if (!agencias.includes(ag)) agencias.push(ag);
    });

    return {
      grupo: conTodas("_grupo", "grupo"),
      direccion: conTodas("_direccion", "direccion"),
      razonSocial: conTodas("_razonSocial", "razonSocial"),
      agencia: agencias, // sin "Todas": es multi-selección por checkboxes
      departamento: conTodas("_departamento", "departamento"),
    };
  }, [rawData, filters, seleccionGraficas]);

  // Formalizadas dentro del rango según su FECHA DE FORMALIZACIÓN, aunque se
  // hayan registrado antes (ej. registrada en septiembre y formalizada en
  // octubre). rawData solo trae lo registrado en el rango, así que se piden
  // aparte a la API con formalizadas_en_rango: true (solo regresa formalizadas).
  // null = todavía no llega; mientras tanto se usa rawData.
  const [rawDataFormalizadas, setRawDataFormalizadas] = useState(null);
  const fetchFormalizadasIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++fetchFormalizadasIdRef.current;
    setRawDataFormalizadas(null);

    if (!filters.startDate || !filters.endDate) return;

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: accessToken,
        modulos: JSON.parse(localStorage.getItem("modulos")) || [],
        usuario_id: localStorage.getItem("userId"),
        agencias: JSON.parse(localStorage.getItem("agencias")) || [],
        startDate: filters.startDate,
        endDate: filters.endDate,
        formalizadas_en_rango: true,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (requestId !== fetchFormalizadasIdRef.current) return;
        setRawDataFormalizadas(
          data?.statusCode === 200 && Array.isArray(data?.data?.resultados)
            ? data.data.resultados.map(normalizeRow)
            : [],
        );
      })
      .catch((error) => {
        if (requestId !== fetchFormalizadasIdRef.current) return;
        setRawDataFormalizadas(null); // si falla, se queda con rawData
        console.error("Error al consultar formalizadas del rango:", error);
      });
  }, [filters.startDate, filters.endDate]);

  // Dataset exclusivo del indicador "% De Formalización": a diferencia del
  // resto del dashboard (que filtra por fecha de registro), aquí una
  // solicitud formalizada cuenta según la fecha en que se formalizó
  // (historial_cambios_status), no la de registro. Así, una solicitud
  // registrada en agosto pero formalizada en septiembre sí aparece al
  // filtrar septiembre, sin afectar ninguna otra tarjeta, gráfica o tabla.
  const dataFormalizadasIndicador = useMemo(() => {
    const hasDateFilter = Boolean(filters.startDate && filters.endDate);
    const start = Moment(filters.startDate, "YYYY-MM-DD", true).startOf("day");
    const end = Moment(filters.endDate, "YYYY-MM-DD", true).endOf("day");

    return (rawDataFormalizadas || rawData).filter((item) => {
      if (!pasaFiltros(item, { ignorarFecha: true })) return false;
      if (!isFormalizada(item)) return false;

      if (hasDateFilter) {
        if (!item._fechaFormalizacion || !item._fechaFormalizacion.isValid()) {
          return false;
        }

        if (
          start.isValid() &&
          end.isValid() &&
          !item._fechaFormalizacion.isBetween(start, end, undefined, "[]")
        ) {
          return false;
        }
      }

      return true;
    });
  }, [rawData, rawDataFormalizadas, filters, seleccionGraficas]);

  const filteredData = useMemo(() => {
    return rawData.filter((item) => pasaFiltros(item));
  }, [rawData, filters, seleccionGraficas]);

  const dataConStatus = useMemo(() => {
    return filteredData.filter(hasStatusReal);
  }, [filteredData]);

  const metrics = useMemo(() => {
    // El primer indicador sí debe considerar todas las solicitudes filtradas,
    // aunque todavía no tengan status. Esto permite comparar contra las que
    // ya tienen status asignado y saber cuántas faltan por atender.
    const total = filteredData.length;
    const conStatus = dataConStatus.length;
    const totalCredito = dataConStatus.filter(isCredito).length;
    const totalContado = dataConStatus.filter(isContado).length;
    const totalArrendamiento = dataConStatus.filter(isArrendamiento).length;
    const autorizadas = dataConStatus.filter(isAutorizada).length;
    const rechazadas = dataConStatus.filter(isRechazada).length;
    const pendientesBuro = dataConStatus.filter(isPendienteBuro).length;
    const baseTasa = baseTasaCredito(totalCredito, pendientesBuro);
    const formalizadas = dataFormalizadasIndicador.length;
    const pruebasManejoSi = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;
    const pruebasManejoNo = dataConStatus.filter(
      (item) => item._realizoPruebaManejo !== "Sí",
    ).length;

    return {
      total,
      conStatus,
      totalCredito,
      totalContado,
      totalArrendamiento,
      autorizadas,
      rechazadas,
      pendientesBuro,
      baseTasa,
      formalizadas,
      pruebasManejoSi,
      pruebasManejoNo,
      pendientesStatus: total - conStatus,
      pctSolicitudesTotales: total
        ? ((conStatus / total) * 100).toFixed(1)
        : "0.0",
      pctPruebasManejo: conStatus
        ? ((pruebasManejoSi / conStatus) * 100).toFixed(1)
        : "0.0",
      pctAprobacion: tasaCredito(autorizadas, totalCredito, pendientesBuro),
      pctRechazo: tasaCredito(rechazadas, totalCredito, pendientesBuro),
    };
  }, [filteredData, dataConStatus, dataFormalizadasIndicador]);

  const formatNumber = (num) => new Intl.NumberFormat("es-MX").format(num);

  const formatCurrency = (num) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    }).format(Number(num || 0));

  const parseMoneyValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;

    let cleaned = String(value)
      .replace(/\s/g, "")
      .replace(/[^0-9,.-]/g, "");

    if (!cleaned) return 0;

    const hasComma = cleaned.includes(",");
    const hasDot = cleaned.includes(".");

    if (hasComma && hasDot) {
      cleaned = cleaned.replace(/,/g, "");
    } else if (hasComma && !hasDot) {
      cleaned = cleaned.replace(/,/g, ".");
    }

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // -----------------------------------------------------------------------
  // Periodo para los comparativos "mes anterior vs actual"
  // -----------------------------------------------------------------------
  //  - Si elegiste fechas: se compara EXACTAMENTE ese rango contra los mismos
  //    días del mes anterior. Ej. 01/10–07/10 vs 01/09–07/09.
  //  - Con el rango por default (mes actual): mes en curso hasta hoy vs
  //    los mismos días del mes anterior.
  const periodoComparativo = useMemo(() => {
    const finBase = Moment(filters.endDate, "YYYY-MM-DD", true);
    const fin = (finBase.isValid() ? finBase : Moment()).clone().endOf("day");
    const inicioBase = Moment(filters.startDate, "YYYY-MM-DD", true);
    const inicio = (
      isRangoDefault || !inicioBase.isValid()
        ? fin.clone().startOf("month")
        : inicioBase
    )
      .clone()
      .startOf("day");

    const inicioAnterior = inicio.clone().subtract(1, "month").startOf("day");
    const finAnterior = fin.clone().subtract(1, "month").endOf("day");

    const fmt = (a, b) =>
      a.isSame(b, "year")
        ? `${a.format("DD/MM")} – ${b.format("DD/MM/YYYY")}`
        : `${a.format("DD/MM/YYYY")} – ${b.format("DD/MM/YYYY")}`;

    return {
      inicio,
      fin,
      inicioAnterior,
      finAnterior,
      dias: fin.clone().startOf("day").diff(inicio, "days") + 1,
      etiquetaActual: fmt(inicio, fin),
      etiquetaAnterior: fmt(inicioAnterior, finAnterior),
    };
  }, [filters.startDate, filters.endDate, isRangoDefault]);

  // Si el periodo anterior no está dentro de lo que ya se descargó, se pide
  // aparte (misma API y mismos parámetros startDate / endDate).
  const [rawDataComparativo, setRawDataComparativo] = useState(null);
  const fetchComparativoIdRef = useRef(0);

  useEffect(() => {
    const cargadoDesde = Moment(filters.startDate, "YYYY-MM-DD", true);
    const dentroDeLoCargado =
      cargadoDesde.isValid() &&
      !periodoComparativo.inicioAnterior.isBefore(cargadoDesde, "day");

    const requestId = ++fetchComparativoIdRef.current;

    if (dentroDeLoCargado) {
      setRawDataComparativo(null); // se usa rawData
      return;
    }

    setRawDataComparativo([]);

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: accessToken,
        modulos: JSON.parse(localStorage.getItem("modulos")) || [],
        usuario_id: localStorage.getItem("userId"),
        agencias: JSON.parse(localStorage.getItem("agencias")) || [],
        startDate: periodoComparativo.inicioAnterior.format("YYYY-MM-DD"),
        endDate: periodoComparativo.finAnterior.format("YYYY-MM-DD"),
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (requestId !== fetchComparativoIdRef.current) return;
        setRawDataComparativo(
          data?.statusCode === 200 && Array.isArray(data?.data?.resultados)
            ? data.data.resultados.map(normalizeRow)
            : [],
        );
      })
      .catch((error) => {
        if (requestId !== fetchComparativoIdRef.current) return;
        setRawDataComparativo([]);
        console.error("Error al consultar periodo comparativo:", error);
      });
  }, [periodoComparativo, filters.startDate]);

  // Solicitudes con status de cada periodo (con todos los filtros menos fecha)
  const dataComparativo = useMemo(() => {
    const enPeriodo = (rows, ini, fin) =>
      rows.filter(
        (item) =>
          item._fecha &&
          item._fecha.isValid() &&
          item._fecha.isBetween(ini, fin, undefined, "[]") &&
          hasStatusReal(item) &&
          pasaFiltros(item, { ignorarFecha: true }),
      );

    return {
      actual: enPeriodo(rawData, periodoComparativo.inicio, periodoComparativo.fin),
      anterior: enPeriodo(
        rawDataComparativo || rawData,
        periodoComparativo.inicioAnterior,
        periodoComparativo.finAnterior,
      ),
    };
  }, [rawData, rawDataComparativo, periodoComparativo, filters, seleccionGraficas]);

  const getAccessoryAmount = (item) => {
    return parseMoneyValue(
      pick(
        item?.utilidad,
        item?.ganancia,
        item?.profit,
        item?.price_promo,
        item?.precio_promo,
        item?.price,
        item?.precio,
        item?.price_base,
        item?.precio_base,
        item?.amount,
        item?.monto,
        item?.total,
      ),
    );
  };

  const truncateText = (value, max = 18) => {
    const text = String(value || "");
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const groupTopNWithOthers = (items = [], topN = 6) => {
    const sorted = [...items].sort((a, b) => b.y - a.y);
    const top = sorted.slice(0, topN);
    const others = sorted.slice(topN);

    if (others.length > 0) {
      const othersTotal = others.reduce((acc, item) => acc + item.y, 0);
      top.push({
        name: "Otros",
        y: othersTotal,
        // Para que al dar clic en "Otros" se filtren todos los que agrupa
        custom: { agrupados: others.map((item) => item.name) },
      });
    }

    return top;
  };

  const percentage = (value, total) => {
    if (!total) return "0.0";
    return ((value / total) * 100).toFixed(1);
  };
  // ---------------------------------------------------------------------
  // Solicitudes por financiera (usa trayectoria_financieras de la API)
  // ---------------------------------------------------------------------
  // - Solicitudes: en cuántas solicitudes participó la financiera.
  // - Cambiadas: se cambió a otra financiera → rechazada por esa financiera.
  // - Actual (última elegida): se separa por status.
  // - Rechazadas total = cambiadas + actuales con status Rechazado.
  // Contado no se cuenta (ahí la financiera es solo para MSI).
  const financieraTabla = useMemo(() => {
    const filas = {};
    let solicitudes = 0;
    let conCambio = 0;
    let sinPrimera = 0;
    let sinFinanciera = 0;

    const fila = (financiera) => {
      if (!filas[financiera]) {
        filas[financiera] = {
          financiera,
          solicitudes: 0,
          primera: 0,
          cambiadas: 0,
          actual: 0,
          autorizado: 0,
          rechazado: 0,
          pendienteBuro: 0,
          otro: 0,
        };
      }
      return filas[financiera];
    };

    filteredData.forEach((item) => {
      if (item._tipoPago === "Contado") return;
      const t = item.trayectoria_financieras;
      if (!t || !Array.isArray(t.usadas) || !t.usadas.length) {
        sinFinanciera += 1;
        return;
      }

      solicitudes += 1;
      if (Array.isArray(t.rechazadas) && t.rechazadas.length) conCambio += 1;
      if (!t.inicial) sinPrimera += 1;

      t.usadas.forEach((f) => {
        fila(f).solicitudes += 1;
      });
      (t.rechazadas || []).forEach((f) => {
        fila(f).cambiadas += 1;
      });
      if (t.inicial) fila(t.inicial).primera += 1;

      if (t.actual) {
        const r = fila(t.actual);
        r.actual += 1;
        if (item._status === "Autorizado") r.autorizado += 1;
        else if (item._status === "Rechazado") r.rechazado += 1;
        else if (item._status === "Pendiente de Buro") r.pendienteBuro += 1;
        else r.otro += 1;
      }
    });

    const rows = Object.values(filas)
      .map((r) => {
        const rechazadasTotal = r.cambiadas + r.rechazado;
        return {
          ...r,
          rechazadasTotal,
          pctRechazo: percentage(rechazadasTotal, r.solicitudes),
        };
      })
      .sort((a, b) => b.solicitudes - a.solicitudes);

    return { rows, solicitudes, conCambio, sinPrimera, sinFinanciera };
  }, [filteredData]);



  // ¿Hay algún filtro o selección activa? (para explicar los "Sin datos")
  const hayFiltrosActivos =
    !isRangoDefault ||
    filters.agencia.length > 0 ||
    filters.grupo !== "Todas" ||
    filters.direccion !== "Todas" ||
    filters.razonSocial !== "Todas" ||
    filters.departamento !== "Todas" ||
    filters.tipoPago !== "Todas" ||
    Object.keys(seleccionGraficas).length > 0;

  // La librería de gráficas ACTUALIZA el chart existente (combina opciones)
  // en lugar de recrearlo. Si una gráfica se creó con "Sin datos" (ejes y
  // leyenda ocultos) y después le llegan datos sin que cambie su key, los
  // ejes/meses y la leyenda se quedaban ocultos. Por eso la key de cada
  // gráfica cambia cuando pasa de "sin datos" a "con datos" y se recrea.
  const esSinDatos = (options) =>
    options?.xAxis?.visible === false && options?.legend?.enabled === false;

  const getNoDataOptions = (height = 320) => ({
    chart: {
      type: "column",
      backgroundColor: chartBg,
      height,
    },
    title: {
      text: hayFiltrosActivos
        ? "Sin datos con los filtros activos"
        : "Sin datos para mostrar",
      style: {
        color: mutedColor,
        fontSize: "14px",
        fontWeight: 600,
      },
    },
    subtitle: hayFiltrosActivos
      ? {
          text: "Esta gráfica no tiene información para tu selección. Quita algún filtro de la barra \"Filtros activos\" para ver más.",
          style: { color: mutedColor, fontSize: "11px" },
        }
      : undefined,
    credits: { enabled: false },
    xAxis: { visible: false },
    yAxis: { visible: false },
    legend: { enabled: false },
    series: [],
  });

  const dataForRealRange = useMemo(() => {
    return rawData.filter(
      (item) =>
        pasaFiltros(item, { ignorarFecha: true }) &&
        hasStatusReal(item) &&
        item._fecha &&
        item._fecha.isValid(),
    );
  }, [rawData, filters, seleccionGraficas]);

  const realDateRange = useMemo(() => {
    if (!dataForRealRange.length) {
      return { min: null, max: null };
    }

    const sortedDates = dataForRealRange
      .map((item) => item._fecha.clone())
      .sort((a, b) => a.valueOf() - b.valueOf());

    return {
      min: sortedDates[0],
      max: sortedDates[sortedDates.length - 1],
    };
  }, [dataForRealRange]);

  const selectedDateRangeText =
    filters.startDate && filters.endDate
      ? `${Moment(filters.startDate).format("DD/MM/YYYY")} a ${Moment(filters.endDate).format("DD/MM/YYYY")}`
      : realDateRange.min && realDateRange.max
        ? `${realDateRange.min.format("DD/MM/YYYY")} a ${realDateRange.max.format("DD/MM/YYYY")}`
        : "Sin fechas disponibles";

  const chartRenderKey = useMemo(() => {
    return [
      dataConStatus.length,
      filteredData.length,
      filters.startDate || "sin-inicio",
      filters.endDate || "sin-fin",
      filters.agencia.join(",") || "todas",
      filters.direccion,
      filters.departamento,
      filters.razonSocial,
      filters.tipoPago,
      filters.grupo,
      JSON.stringify(seleccionGraficas),
    ].join("|");
  }, [
    filters.grupo,
    seleccionGraficas,
    dataConStatus.length,
    filteredData.length,
    filters.startDate,
    filters.endDate,
    filters.agencia,
    filters.direccion,
    filters.departamento,
    filters.razonSocial,
    filters.tipoPago,
  ]);

  // ---------------------------------------------------------------------
  // Clic en gráficas → filtros dinámicos + desglose por asesor
  // ---------------------------------------------------------------------
  // Las dimensiones que ya tienen filtro arriba mueven ese filtro; las demás
  // se agregan como "selección en gráficas" (chips). Dar clic otra vez en lo
  // mismo lo quita. Siempre abre el panel con el desglose por asesor.
  const FILTROS_CON_SELECT = [
    "grupo",
    "direccion",
    "razonSocial",
    "departamento",
    "tipoPago",
  ];

  // Refs con el estado más reciente (para el aviso y "Deshacer")
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const seleccionRef = useRef(seleccionGraficas);
  seleccionRef.current = seleccionGraficas;
  const avisoSnapshotRef = useRef(null);
  const [avisoFiltro, setAvisoFiltro] = useState(null);

  useEffect(() => {
    if (!avisoFiltro) return undefined;
    const t = setTimeout(() => setAvisoFiltro(null), 7000);
    return () => clearTimeout(t);
  }, [avisoFiltro]);

  const deshacerUltimoFiltro = () => {
    const snap = avisoSnapshotRef.current;
    if (!snap) return;
    const cambiaFecha =
      snap.filters.startDate !== filtersRef.current.startDate ||
      snap.filters.endDate !== filtersRef.current.endDate;
    setFilters(snap.filters);
    setSeleccionGraficas(snap.seleccion);
    if (cambiaFecha) setDatePickerResetKey((prev) => prev + 1);
    setAvisoFiltro(null);
  };

  const NOMBRES_FILTRO = {
    agencia: "Agencia",
    grupo: "Grupo",
    direccion: "Dirección",
    razonSocial: "Razón social",
    departamento: "Departamento",
    tipoPago: "Tipo de pago",
  };

  const etiquetaSeleccion = (sel) => {
    if (sel.label) return sel.label;
    if (sel.dim === "mes") {
      return `Mes: ${Moment(sel.value, "YYYY-MM").format("MMMM YYYY")}`;
    }
    return `${NOMBRES_FILTRO[sel.dim] || sel.dim}: ${sel.value}`;
  };

  // ¿Ese clic quita algo que ya estaba seleccionado?
  const esSeleccionActiva = (sel) => {
    const f = filtersRef.current;
    if (sel.dim === "agencia") {
      return f.agencia.length === 1 && f.agencia[0] === sel.value;
    }
    if (sel.dim === "mes") return false;
    if (Object.prototype.hasOwnProperty.call(NOMBRES_FILTRO, sel.dim)) {
      return f[sel.dim] === sel.value;
    }
    const actual = seleccionRef.current[sel.dim];
    return (
      Boolean(actual) &&
      JSON.stringify(actual.value) === JSON.stringify(sel.value)
    );
  };

  const aplicarSeleccionGrafica = (selecciones = []) => {
    const lista = selecciones.filter(
      (sel) => sel && sel.value !== undefined && sel.value !== null,
    );
    if (!lista.length) return;

    // Al tocar un mes se mueve el rango de fechas a ese mes
    const mes = lista.find((sel) => sel.dim === "mes");
    const resto = lista.filter((sel) => sel.dim !== "mes");

    setFilters((prev) => {
      const next = { ...prev };

      if (mes) {
        const inicio = Moment(mes.value, "YYYY-MM").startOf("month");
        const finMes = inicio.clone().endOf("month");
        next.startDate = inicio.format("YYYY-MM-DD");
        next.endDate = (finMes.isAfter(Moment()) ? Moment() : finMes).format(
          "YYYY-MM-DD",
        );
      }

      resto.forEach((sel) => {
        if (sel.dim === "agencia") {
          const yaSolo =
            prev.agencia.length === 1 && prev.agencia[0] === sel.value;
          next.agencia = yaSolo ? [] : [sel.value];
        } else if (FILTROS_CON_SELECT.includes(sel.dim)) {
          next[sel.dim] = prev[sel.dim] === sel.value ? "Todas" : sel.value;
        }
      });

      return next;
    });

    const paraChips = resto.filter(
      (sel) => sel.dim !== "agencia" && !FILTROS_CON_SELECT.includes(sel.dim),
    );

    if (paraChips.length) {
      setSeleccionGraficas((prev) => {
        const next = { ...prev };
        paraChips.forEach((sel) => {
          const actual = prev[sel.dim];
          const igual =
            actual &&
            JSON.stringify(actual.value) === JSON.stringify(sel.value);
          if (igual) {
            delete next[sel.dim];
          } else {
            next[sel.dim] = { value: sel.value, label: sel.label };
          }
        });
        return next;
      });
    }

    if (mes) resetPickerUi();

    // Aviso para que quede claro que TODO el tablero se filtró (el panel de
    // asesores ya no se abre solo; se abre con "Ver desglose por asesor").
    avisoSnapshotRef.current = {
      filters: filtersRef.current,
      seleccion: seleccionRef.current,
    };
    const quitado = lista.every((sel) => esSeleccionActiva(sel));
    setAvisoFiltro({
      id: Date.now(),
      quitado,
      texto: lista.map(etiquetaSeleccion).join(" · "),
    });
  };

  // Ref para que los handlers de Highcharts siempre usen la versión actual
  const aplicarSeleccionRef = useRef(aplicarSeleccionGrafica);
  aplicarSeleccionRef.current = aplicarSeleccionGrafica;

  const quitarSeleccionGrafica = (dim) => {
    setSeleccionGraficas((prev) => {
      const next = { ...prev };
      delete next[dim];
      return next;
    });
  };

  // Agrega el clic a cualquier opción de Highcharts. `resolver(point)` regresa
  // la(s) selección(es) { dim, value, label } o null si ese punto no filtra.
  const conDrill = (options, resolver) => {
    if (!options || !Array.isArray(options.series) || !options.series.length) {
      return options;
    }

    return {
      ...options,
      plotOptions: {
        ...(options.plotOptions || {}),
        series: {
          ...(options.plotOptions?.series || {}),
          cursor: "pointer",
          point: {
            ...(options.plotOptions?.series?.point || {}),
            events: {
              ...(options.plotOptions?.series?.point?.events || {}),
              click: function () {
                const result = resolver(this);
                if (!result) return;
                aplicarSeleccionRef.current(
                  Array.isArray(result) ? result : [result],
                );
              },
            },
          },
        },
      },
    };
  };

  const customDe = (point) => point?.options?.custom || point?.custom || {};

  // Para pies/barras agrupadas con "Otros"
  const valorConOtros = (point) => {
    const agrupados = customDe(point).agrupados;
    return agrupados && agrupados.length ? agrupados : point.name;
  };

  const statusChartData = useMemo(() => {
    if (filters.tipoPago !== "Todas" && filters.tipoPago !== "Crédito") {
      return [];
    }

    return dataConStatus.filter((item) => item._tipoPago === "Crédito");
  }, [dataConStatus, filters.tipoPago]);

  const contadoStatusChartData = useMemo(() => {
    if (filters.tipoPago !== "Todas" && filters.tipoPago !== "Contado") {
      return [];
    }

    return dataConStatus.filter((item) => item._tipoPago === "Contado");
  }, [dataConStatus, filters.tipoPago]);

  const arrendamientoStatusChartData = useMemo(() => {
    if (filters.tipoPago !== "Todas" && filters.tipoPago !== "Arrendamiento") {
      return [];
    }

    return dataConStatus.filter((item) => item._tipoPago === "Arrendamiento");
  }, [dataConStatus, filters.tipoPago]);

  // Antes se usaba negación (!== "Contado", !== "Crédito"), lo cual
  // funcionaba mientras solo existían 2 tipos de pago. Al agregar
  // "Arrendamiento" como tercer valor, la negación hacía que los bloques
  // de crédito/contado siguieran mostrándose (vacíos) al filtrar por
  // Arrendamiento. Se usa comparación explícita por valor.
  const shouldShowStatusChart =
    filters.tipoPago === "Todas" || filters.tipoPago === "Crédito";
  const shouldShowContadoStatusChart =
    filters.tipoPago === "Todas" || filters.tipoPago === "Contado";
  const shouldShowArrendamientoStatusChart =
    filters.tipoPago === "Todas" || filters.tipoPago === "Arrendamiento";
  const shouldShowCreditOnlyBlocks =
    filters.tipoPago === "Todas" || filters.tipoPago === "Crédito";

  const statusSubstatusChartOptions = useMemo(() => {
    if (!statusChartData.length) return getNoDataOptions(420);

    const statuses = STATUS_ORDER;
    const orderedSubstatuses = SUBSTATUS_ORDER;

    const substatusColors = {
      "Sin substatus": colorSet.gris,
      Analisis: colorSet.azulOscuro,
      "Condicionado Viable": colorSet.azul,
      "Condicionado No Viable": colorSet.rojo,
      Incompleto: colorSet.amarillo,
      Facturado: colorSet.celeste,
      Formalizado: colorSet.turquesa,
      "Aprobado Sin Formalizar": colorSet.naranja,
      Rechazado: colorSet.rosa,
    };

    const totalsByStatus = statuses.map(
      (status) =>
        statusChartData.filter((item) => item._status === status).length,
    );

    const series = orderedSubstatuses
      .map((sub) => ({
        name: sub,
        color: substatusColors[sub] || colorSet.gris,
        data: statuses.map((status) => {
          return statusChartData.filter(
            (item) => item._status === status && item._subStatus === sub,
          ).length;
        }),
      }))
      .filter((serie) => serie.data.some((value) => value > 0));

    if (!series.length) return getNoDataOptions(420);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 420,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      xAxis: {
        categories: statuses,
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        stackLabels: {
          enabled: true,
          style: {
            color: textColor,
            textOutline: "none",
            fontWeight: 700,
          },
        },
        allowDecimals: false,
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const total = totalsByStatus[this.points[0].point.index] || 0;
          let html = `<b>${this.x}</b><br/>Total: <b>${total}</b><br/><br/>`;

          this.points.forEach((point) => {
            if (point.y > 0) {
              html += `
                <span style="color:${point.color}">\u25CF</span>
                ${point.series.name}: <b>${point.y}</b> (${percentage(point.y, total)}%)<br/>
              `;
            }
          });

          return html;
        },
      },
      plotOptions: {
        series: {
          stacking: "normal",
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.08,
          dataLabels: {
            enabled: false,
          },
        },
      },
      series,
    };
  }, [statusChartData, textColor, gridColor, mutedColor]);

  const contadoStatusChartOptions = useMemo(() => {
    if (!contadoStatusChartData.length) return getNoDataOptions(340);

    const statuses = CONTADO_STATUS_ORDER;
    const counts = statuses.map((status) => ({
      name: status,
      y: contadoStatusChartData.filter((item) => item._status === status)
        .length,
      color: status === "Formalizado" ? colorSet.turquesa : colorSet.naranja,
    }));

    const hasValues = counts.some((item) => item.y > 0);
    if (!hasValues) return getNoDataOptions(340);

    const total = contadoStatusChartData.length;

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.key}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del contado`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y}`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Status contado",
          data: counts,
        },
      ],
    };
  }, [contadoStatusChartData, textColor, gridColor]);

  const arrendamientoStatusChartOptions = useMemo(() => {
    if (!arrendamientoStatusChartData.length) return getNoDataOptions(340);

    const statuses = ARRENDAMIENTO_STATUS_ORDER;
    const statusColors = {
      Cotización: colorSet.naranja,
      "Pendiente de Autorización": colorSet.azul,
      Autorizado: colorSet.turquesa,
      Rechazado: colorSet.rojo,
      Formalizado: colorSet.turquesa,
    };

    const counts = statuses.map((status) => ({
      name: status,
      y: arrendamientoStatusChartData.filter((item) => item._status === status)
        .length,
      color: statusColors[status] || colorSet.gris,
    }));

    const hasValues = counts.some((item) => item.y > 0);
    if (!hasValues) return getNoDataOptions(340);

    const total = arrendamientoStatusChartData.length;

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.key}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del arrendamiento`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y}`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Status arrendamiento",
          data: counts,
        },
      ],
    };
  }, [arrendamientoStatusChartData, textColor, gridColor]);

  const fuenteChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._fuente] = (counts[item._fuente] || 0) + 1;
    });

    const rawFuenteData = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, y]) => ({ name, y }));

    const data = groupTopNWithOthers(rawFuenteData, 6);

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      subtitle: {
        text: `Total: ${formatNumber(dataConStatus.length)}`,
        align: "center",
        verticalAlign: "middle",
        y: 10,
        style: {
          color: textColor,
          fontSize: "16px",
          fontWeight: 700,
        },
      },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          showInLegend: true,
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y <= 0) return null;
              return this.percentage >= 6
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: "Fuente",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const financieraChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._financiera] = (counts[item._financiera] || 0) + 1;
    });

    const rawFinancieraData = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, y]) => ({ name, y }));

    const data = groupTopNWithOthers(rawFinancieraData, 6);

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      subtitle: {
        text: `Total: ${formatNumber(dataConStatus.length)}`,
        align: "center",
        verticalAlign: "middle",
        y: 10,
        style: {
          color: textColor,
          fontSize: "16px",
          fontWeight: 700,
        },
      },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          showInLegend: true,
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y <= 0) return null;
              return this.percentage >= 6
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: "Financiera",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const tipoPagoComparacionChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const contado = dataConStatus.filter(
      (item) => item._tipoPago === "Contado",
    ).length;

    const credito = dataConStatus.filter(
      (item) => item._tipoPago === "Crédito",
    ).length;

    const arrendamiento = dataConStatus.filter(
      (item) => item._tipoPago === "Arrendamiento",
    ).length;

    const total = contado + credito + arrendamiento;

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: ["Contado", "Crédito", "Arrendamiento"],
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.x}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del total`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y}`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          data: [
            { y: contado, color: colorSet.naranja, custom: { full: "Contado" } },
            { y: credito, color: colorSet.azul, custom: { full: "Crédito" } },
            {
              y: arrendamiento,
              color: colorSet.morado,
              custom: { full: "Arrendamiento" },
            },
          ],
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  // Dona Sí/No reutilizable para las gráficas de venta en Babilonia y NR.
  const buildDonutOptions = ({ data, centerText, unidad = "solicitudes" }) => {
    const total = data.reduce((acc, item) => acc + item.y, 0);
    if (!total) return getNoDataOptions(340);

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      subtitle: {
        text: centerText,
        align: "center",
        verticalAlign: "middle",
        y: 0,
        useHTML: true,
        style: {
          color: textColor,
          fontSize: "15px",
          fontWeight: 700,
          textAlign: "center",
        },
      },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat: `<b>{point.y}</b> ${unidad} ({point.percentage:.1f}%)`,
      },
      plotOptions: {
        pie: {
          innerSize: "62%",
          showInLegend: true,
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y <= 0) return null;
              return `${this.y} (${this.percentage.toFixed(1)}%)`;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "11px",
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          data: data.filter((item) => item.y > 0),
        },
      ],
    };
  };

  // Se usa filteredData (todas las solicitudes del rango, con o sin status)
  // porque venta_babilonia describe dónde se hizo la venta, no su avance.
  const ventaBabiloniaChartOptions = useMemo(() => {
    const si = filteredData.filter(
      (item) => item._ventaBabilonia === "Sí",
    ).length;
    const no = filteredData.filter(
      (item) => item._ventaBabilonia === "No",
    ).length;
    const sinRegistrar = filteredData.filter(
      (item) => item._ventaBabilonia === "Sin registrar",
    ).length;

    const data = [
      {
        name: "Sí, en Babilonia",
        y: si,
        color: colorSet.turquesa,
        custom: { valor: "Sí" },
      },
      { name: "No", y: no, color: colorSet.rojo, custom: { valor: "No" } },
    ];

    // Solo aparece si hay registros sin el campo (no debería haber)
    if (sinRegistrar > 0) {
      data.push({
        name: "Sin registrar",
        y: sinRegistrar,
        color: colorSet.gris,
        custom: { valor: "Sin registrar" },
      });
    }

    return buildDonutOptions({
      data,
      centerText: `${percentage(si, filteredData.length)}%<br/><span style="font-size:11px;font-weight:600">en Babilonia</span>`,
    });
  }, [filteredData, textColor]);

  // Solicitudes tardías por agencia, apiladas por el mes al que corresponden.
  // Se usa filteredData (con o sin status) porque lo tardío es la captura,
  // no el avance de la solicitud.
  const solicitudesTardiasChartOptions = useMemo(() => {
    const tardias = filteredData.filter((item) => item._esTardia);

    if (!tardias.length) return getNoDataOptions(360);

    const conteoAgencia = {};
    tardias.forEach((item) => {
      conteoAgencia[item._agencia] = (conteoAgencia[item._agencia] || 0) + 1;
    });
    const agencias = Object.keys(conteoAgencia).sort(
      (a, b) => conteoAgencia[b] - conteoAgencia[a],
    );

    const meses = Array.from(
      new Set(
        tardias
          .filter((item) => item._fecha)
          .map((item) => item._fecha.format("YYYY-MM")),
      ),
    ).sort();

    const paleta = [
      colorSet.naranja,
      colorSet.morado,
      colorSet.azul,
      colorSet.rojo,
      colorSet.amarillo,
      colorSet.celeste,
    ];

    const series = meses.map((mes, idx) => ({
      name: Moment(mes, "YYYY-MM").format("MMMM YYYY"),
      color: paleta[idx % paleta.length],
      data: agencias.map(
        (agencia) =>
          tardias.filter(
            (item) =>
              item._agencia === agencia &&
              item._fecha &&
              item._fecha.format("YYYY-MM") === mes,
          ).length,
      ),
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      subtitle: {
        text: `${formatNumber(tardias.length)} de ${formatNumber(
          filteredData.length,
        )} solicitudes (${percentage(
          tardias.length,
          filteredData.length,
        )}%) se capturaron tarde`,
        style: { color: textColor, fontSize: "12px" },
      },
      credits: { enabled: false },
      xAxis: {
        categories: agencias,
        labels: {
          rotation: agencias.length > 6 ? -35 : 0,
          style: { color: textColor, fontSize: "10px" },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: {
          text: "Solicitudes tardías",
          style: { color: textColor },
        },
        labels: { style: { color: textColor } },
        gridLineColor: gridColor,
        stackLabels: {
          enabled: true,
          style: { color: textColor, textOutline: "none" },
        },
      },
      legend: {
        itemStyle: { color: textColor, fontSize: "11px" },
      },
      tooltip: { shared: true },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
        },
      },
      series,
    };
  }, [filteredData, textColor, gridColor]);

  // Igual que venta en Babilonia: se toma todo el rango (con o sin status),
  // porque el flujo NR se define al cotizar, antes de asignar status.
  const ventaNrChartOptions = useMemo(() => {
    const conNr = filteredData.filter((item) => item._esVentaNr).length;
    const sinNr = filteredData.length - conNr;

    return buildDonutOptions({
      data: [
        {
          name: "Con NR (Procotiza)",
          y: conNr,
          color: colorSet.azulOscuro,
          custom: { valor: true },
        },
        {
          name: "Flujo Babilonia",
          y: sinNr,
          color: colorSet.celeste,
          custom: { valor: false },
        },
      ],
      centerText: `${formatNumber(conNr)}<br/><span style="font-size:11px;font-weight:600">con NR</span>`,
    });
  }, [filteredData, textColor]);

  const pruebaManejoChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const si = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;
    const no = dataConStatus.filter(
      (item) => item._realizoPruebaManejo !== "Sí",
    ).length;
    const total = si + no;

    const data = [
      {
        name: "Sí realizó",
        y: si,
        color: colorSet.turquesa,
        custom: { valor: "Sí" },
      },
      { name: "No realizó", y: no, color: colorSet.rojo, custom: { valor: "No" } },
    ];

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${this.key}</b><br/>Solicitudes: <b>${this.y}</b><br/>${percentage(this.y, total)}% del total`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return `${this.y} (${percentage(this.y, total)}%)`;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Prueba de manejo",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const planesSeleccionadosChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(360);

    const counts = {
      Elite: 0,
      Gold: 0,
      Platinum: 0,
      "Plan cliente": 0,
    };

    dataConStatus.forEach((item) => {
      item._selectedPlanTitles.forEach((planTitle) => {
        const normalized = normalizePlanTitle(planTitle);
        counts[normalized] = (counts[normalized] || 0) + 1;
      });
    });

    const data = PLANES_ORDER.map((plan) => ({
      name: plan,
      y: counts[plan] || 0,
      color:
        plan === "Elite"
          ? colorSet.azulOscuro
          : plan === "Gold"
            ? colorSet.amarillo
            : plan === "Platinum"
              ? colorSet.morado
              : colorSet.turquesa,
    }));

    const hasValues = data.some((item) => item.y > 0);
    if (!hasValues) return getNoDataOptions(360);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Selecciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const pct = percentage(this.y, dataConStatus.length);
          return `<b>${this.key}</b><br/>Selecciones: <b>${this.y}</b><br/>${pct}% de las solicitudes filtradas`;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Planes",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const topAccesoriosGarantiasChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const counts = {};

    dataConStatus.forEach((item) => {
      item._selectedAccessories.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!counts[key]) {
          counts[key] = {
            name: title,
            type,
            y: 0,
          };
        }

        counts[key].y += 1;
      });
    });

    const data = Object.values(counts)
      .sort((a, b) => b.y - a.y)
      .slice(0, 5)
      .map((item) => ({
        ...item,
        color:
          item.type === "garantias"
            ? colorSet.naranja
            : item.type === "accesorios"
              ? colorSet.azul
              : colorSet.gris,
      }));

    if (!data.length) return getNoDataOptions(380);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 42)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Selecciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const row = data[this.point.index];
          return `
            <b>${row?.name || this.category}</b><br/>
            Tipo: <b>${row?.type || "sin tipo"}</b><br/>
            Selecciones: <b>${this.y}</b>
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Selecciones",
          data: data.map((item) => ({
            y: item.y,
            color: item.color,
            custom: { full: item.name },
          })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const gananciasAccesoriosGarantiasResumen = useMemo(() => {
    const {
      inicio: currentStart,
      fin: currentEnd,
      inicioAnterior: previousStart,
      finAnterior: previousEnd,
    } = periodoComparativo;

    const emptyTotals = () => ({
      accesorios: 0,
      garantias: 0,
      total: 0,
      accesoriosCount: 0,
      garantiasCount: 0,
      totalCount: 0,
    });

    const getTotals = (items = []) => {
      const totals = emptyTotals();

      items.forEach((item) => {
        const selectedItems = Array.isArray(item._selectedAccessories)
          ? item._selectedAccessories
          : [];

        selectedItems.forEach((acc) => {
          const type = normalizeText(acc?.type || "sin tipo").toLowerCase();

          if (!ACCESORIO_GARANTIA_TIPOS_ORDER.includes(type)) return;

          const amount = getAccessoryAmount(acc);

          totals[type] += amount;
          totals.total += amount;
          totals[`${type}Count`] += 1;
          totals.totalCount += 1;
        });
      });

      return totals;
    };

    const currentData = dataComparativo.actual;
    const previousData = dataComparativo.anterior;

    const currentTotals = getTotals(currentData);
    const previousTotals = getTotals(previousData);

    const diffTotal = currentTotals.total - previousTotals.total;
    const diffPct = previousTotals.total
      ? ((diffTotal / previousTotals.total) * 100).toFixed(1)
      : currentTotals.total
        ? "100.0"
        : "0.0";

    return {
      currentLabel: periodoComparativo.etiquetaActual,
      previousLabel: periodoComparativo.etiquetaAnterior,
      currentTotals,
      previousTotals,
      diffTotal,
      diffPct,
      currentStart,
      currentEnd,
      previousStart,
      previousEnd,
    };
  }, [dataComparativo, periodoComparativo]);

  const gananciasAccesoriosGarantiasRows = useMemo(() => {
    const { currentTotals, previousTotals } =
      gananciasAccesoriosGarantiasResumen;

    return [
      {
        label: "Accesorios",
        key: "accesorios",
        current: currentTotals.accesorios,
        previous: previousTotals.accesorios,
        currentCount: currentTotals.accesoriosCount,
        previousCount: previousTotals.accesoriosCount,
      },
      {
        label: "Garantías",
        key: "garantias",
        current: currentTotals.garantias,
        previous: previousTotals.garantias,
        currentCount: currentTotals.garantiasCount,
        previousCount: previousTotals.garantiasCount,
      },
      {
        label: "Total",
        key: "total",
        current: currentTotals.total,
        previous: previousTotals.total,
        currentCount: currentTotals.totalCount,
        previousCount: previousTotals.totalCount,
      },
    ].map((row) => {
      const diff = row.current - row.previous;
      const diffPct = row.previous
        ? ((diff / row.previous) * 100).toFixed(1)
        : row.current
          ? "100.0"
          : "0.0";

      return {
        ...row,
        diff,
        diffPct,
      };
    });
  }, [gananciasAccesoriosGarantiasResumen]);

  const gananciasAccesoriosGarantiasChartOptions = useMemo(() => {
    const resumen = gananciasAccesoriosGarantiasResumen;
    const hasValues = gananciasAccesoriosGarantiasRows.some(
      (row) => row.current > 0 || row.previous > 0,
    );

    if (!hasValues) return getNoDataOptions(390);

    const categories = gananciasAccesoriosGarantiasRows.map((row) => row.label);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 390,
      },
      title: { text: null },
      subtitle: {
        text: `Comparativo ${resumen.previousLabel} vs ${resumen.currentLabel}`,
        style: {
          color: mutedColor,
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      credits: { enabled: false },
      xAxis: {
        categories,
        labels: {
          style: {
            color: textColor,
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Ganancia / monto vendido",
          style: { color: textColor },
        },
        labels: {
          formatter: function () {
            return formatCurrency(this.value);
          },
          style: { color: textColor },
        },
        gridLineColor: gridColor,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;
          const row = gananciasAccesoriosGarantiasRows[idx];
          if (!row) return false;

          let html = `<b>${row.label}</b><br/>`;

          this.points.forEach((point) => {
            html += `
              <span style="color:${point.color}">\u25CF</span>
              ${point.series.name}: <b>${formatCurrency(point.y)}</b><br/>
            `;
          });

          html += `<br/>Diferencia: <b>${formatCurrency(row.diff)}</b> (${row.diffPct}%)`;
          html += `<br/>Productos vendidos: <b>${formatNumber(row.currentCount)}</b> actual vs <b>${formatNumber(row.previousCount)}</b> anterior`;

          return html;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.y > 0 ? formatCurrency(this.y) : null;
            },
            style: {
              textOutline: "none",
              fontWeight: 700,
              fontSize: "10px",
            },
          },
        },
      },
      series: [
        {
          name: resumen.previousLabel,
          color: colorSet.gris,
          data: gananciasAccesoriosGarantiasRows.map((row) => row.previous),
        },
        {
          name: resumen.currentLabel,
          color: colorSet.azul,
          data: gananciasAccesoriosGarantiasRows.map((row) => row.current),
        },
      ],
    };
  }, [
    gananciasAccesoriosGarantiasResumen,
    gananciasAccesoriosGarantiasRows,
    textColor,
    mutedColor,
    gridColor,
  ]);

  const agenciaDepartamentoChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(380);

    const agencias = Array.from(
      new Set(dataConStatus.map((item) => item._agencia)),
    );

    const departamentos = ["(En blanco)", "Nuevos", "Seminuevos"];
    const departmentColors = {
      "(En blanco)": colorSet.azulOscuro,
      Nuevos: colorSet.naranja,
      Seminuevos: colorSet.celeste,
    };

    const series = departamentos.map((dep) => ({
      name: dep,
      color: departmentColors[dep],
      data: agencias.map((agencia) => {
        return dataConStatus.filter(
          (item) => item._agencia === agencia && item._departamento === dep,
        ).length;
      }),
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: agencias,
        labels: {
          rotation: -35,
          style: {
            color: textColor,
            fontSize: "10px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Total solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        stackLabels: {
          enabled: true,
          style: {
            color: textColor,
            textOutline: "none",
          },
        },
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
        },
      },
      series,
    };
  }, [dataConStatus, textColor, gridColor]);

  const acumuladoChartOptions = useMemo(() => {
    // Compara el periodo elegido contra los mismos días del mes anterior
    // (ver periodoComparativo). Ej. 01/10–07/10 vs 01/09–07/09.
    const {
      inicio,
      inicioAnterior,
      dias,
      etiquetaActual,
      etiquetaAnterior,
    } = periodoComparativo;

    if (!dataComparativo.actual.length && !dataComparativo.anterior.length) {
      return getNoDataOptions(360);
    }

    const currentDaily = Array(dias).fill(0);
    const prevDaily = Array(dias).fill(0);

    dataComparativo.actual.forEach((item) => {
      const idx = item._fecha.clone().startOf("day").diff(inicio, "days");
      if (idx >= 0 && idx < dias) currentDaily[idx] += 1;
    });

    dataComparativo.anterior.forEach((item) => {
      const idx = item._fecha
        .clone()
        .startOf("day")
        .diff(inicioAnterior, "days");
      if (idx >= 0 && idx < dias) prevDaily[idx] += 1;
    });

    const fechasActual = Array.from({ length: dias }, (_, i) =>
      inicio.clone().add(i, "days"),
    );
    const fechasAnterior = Array.from({ length: dias }, (_, i) =>
      inicioAnterior.clone().add(i, "days"),
    );

    const accumulated = (arr) => {
      let sum = 0;
      return arr.map((v) => {
        sum += v;
        return sum;
      });
    };

    return {
      chart: {
        type: "line",
        backgroundColor: chartBg,
        height: 360,
      },
      title: { text: null },
      subtitle: {
        text: `${etiquetaAnterior} vs ${etiquetaActual}`,
        style: {
          color: mutedColor,
          fontSize: "11px",
          fontWeight: 600,
        },
      },
      credits: { enabled: false },
      xAxis: {
        categories: fechasActual.map((f) => f.format("DD/MM")),
        labels: {
          style: { color: textColor },
        },
        lineColor: gridColor,
      },
      yAxis: {
        title: {
          text: "Acumulado",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const i = this.points[0].point.index;
          const actual = this.points.find((p) => p.series.index === 0);
          const anterior = this.points.find((p) => p.series.index === 1);
          return `
            <b>Día ${i + 1} del periodo</b><br/>
            ${fechasActual[i].format("DD/MM/YYYY")}: <b>${actual ? actual.y : 0}</b> acumuladas<br/>
            ${fechasAnterior[i].format("DD/MM/YYYY")}: <b>${anterior ? anterior.y : 0}</b> acumuladas
          `;
        },
      },
      plotOptions: {
        series: {
          marker: {
            enabled: dias <= 31,
            radius: 3,
          },
          lineWidth: 3,
        },
      },
      series: [
        {
          name: `Actual (${etiquetaActual})`,
          color: colorSet.azulOscuro,
          data: accumulated(currentDaily),
        },
        {
          name: `Mes anterior (${etiquetaAnterior})`,
          color: colorSet.azul,
          data: accumulated(prevDaily),
        },
      ],
    };
  }, [dataComparativo, periodoComparativo, textColor, mutedColor, gridColor]);

  const asesorRows = useMemo(() => {
    // dataConStatus ya respeta los filtros actuales, incluyendo tipo de pago.
    // Cuando el filtro está en "Todas", aquí deben entrar crédito, contado
    // y arrendamiento, para que el total por asesor coincida con el total
    // real de solicitudes con status.
    const baseData = dataConStatus;

    const total = baseData.length || 1;
    const map = {};

    baseData.forEach((item) => {
      const asesor = item._asesor;

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          total: 0,
          creditos: 0,
          contados: 0,
          arrendamientos: 0,
          autorizadas: 0,
          rechazadas: 0,
          pendientesBuro: 0,
          formalizadas: 0,
          pruebasManejo: 0,
          tardias: 0,
        };
      }

      map[asesor].total += 1;
      if (item._esTardia) map[asesor].tardias += 1;

      if (isCredito(item)) map[asesor].creditos += 1;
      if (isContado(item)) map[asesor].contados += 1;
      if (isArrendamiento(item)) map[asesor].arrendamientos += 1;
      if (isAutorizada(item)) map[asesor].autorizadas += 1;
      if (isRechazada(item)) map[asesor].rechazadas += 1;
      if (item._status === "Pendiente de Buro") map[asesor].pendientesBuro += 1;
      if (isFormalizada(item)) map[asesor].formalizadas += 1;
      if (item._realizoPruebaManejo === "Sí") map[asesor].pruebasManejo += 1;
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        pctAutorizadas: row.creditos
          ? ((row.autorizadas / row.creditos) * 100).toFixed(1)
          : "0.0",
        pctRechazadas: row.creditos
          ? ((row.rechazadas / row.creditos) * 100).toFixed(1)
          : "0.0",
        pctParticipacion: ((row.total / total) * 100).toFixed(1),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 15);
  }, [dataConStatus]);

  const asesorPaquetesRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const asesor = item._asesor || "EXTERNO";

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          totalPaquetesVendidos: 0,
        };

        PLANES_RANKING_ORDER.forEach((plan) => {
          map[asesor][plan] = 0;
        });
      }

      const selectedPlans = Array.isArray(item._selectedPlanTitles)
        ? item._selectedPlanTitles
        : [];

      selectedPlans.forEach((planTitle) => {
        const normalized = normalizePlanTitle(planTitle);

        if (!map[asesor][normalized] && map[asesor][normalized] !== 0) {
          map[asesor][normalized] = 0;
        }

        map[asesor][normalized] += 1;
        map[asesor].totalPaquetesVendidos += 1;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.totalPaquetesVendidos - a.totalPaquetesVendidos)
      .slice(0, 15);
  }, [dataConStatus]);

  const accesoriosGarantiasColumns = useMemo(() => {
    const counts = {};

    dataConStatus.forEach((item) => {
      const selectedItems = Array.isArray(item._selectedAccessories)
        ? item._selectedAccessories
        : [];

      selectedItems.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!counts[key]) {
          counts[key] = {
            key,
            title,
            type,
            total: 0,
          };
        }

        counts[key].total += 1;
      });
    });

    return Object.values(counts)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [dataConStatus]);

  const asesorAccesoriosGarantiasMatrixRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const asesor = item._asesor || "EXTERNO";
      const selectedItems = Array.isArray(item._selectedAccessories)
        ? item._selectedAccessories
        : [];

      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          totalItemsVendidos: 0,
        };
      }

      selectedItems.forEach((acc) => {
        const title = normalizeAccessoryTitle(acc?.title);
        const type = normalizeText(acc?.type || "sin tipo").toLowerCase();
        const key = `${title}|||${type}`;

        if (!(key in map[asesor])) {
          map[asesor][key] = 0;
        }

        map[asesor][key] += 1;
        map[asesor].totalItemsVendidos += 1;
      });
    });

    return Object.values(map)
      .sort((a, b) => b.totalItemsVendidos - a.totalItemsVendidos)
      .slice(0, 15);
  }, [dataConStatus]);

  const formalizacionesFinancieraChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(320);

    const counts = {};
    dataConStatus.filter(isFormalizada).forEach((item) => {
      counts[item._financiera] = (counts[item._financiera] || 0) + 1;
    });

    const data = groupTopNWithOthers(
      Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([name, y]) => ({ name, y })),
      5,
    );

    return {
      chart: {
        type: "pie",
        backgroundColor: chartBg,
        height: 320,
      },
      title: { text: null },
      credits: { enabled: false },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        pointFormat:
          "<b>{point.y}</b> formalizaciones ({point.percentage:.1f}%)",
      },
      plotOptions: {
        pie: {
          innerSize: "55%",
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.percentage >= 7
                ? `${this.point.name}<br/>${this.y} (${this.percentage.toFixed(1)}%)`
                : null;
            },
            style: {
              textOutline: "none",
              color: textColor,
              fontSize: "10px",
            },
          },
          showInLegend: true,
        },
      },
      series: [
        {
          name: "Formalizaciones",
          data,
        },
      ],
    };
  }, [dataConStatus, textColor]);

  const direccionRows = useMemo(() => {
    const map = {};

    dataConStatus.forEach((item) => {
      const key = item._direccion;

      if (!map[key]) {
        map[key] = {
          direccion: key,
          total: 0,
          autorizadas: 0,
          formalizaciones: 0,
        };
      }

      map[key].total += 1;
      if (isAutorizada(item)) map[key].autorizadas += 1;
      if (isFormalizada(item)) map[key].formalizaciones += 1;
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        pctAprobacion: row.total
          ? ((row.autorizadas / row.total) * 100).toFixed(1)
          : "0.0",
      }))
      .sort((a, b) => b.formalizaciones - a.formalizaciones)
      .slice(0, 8);
  }, [dataConStatus]);

  const statusSimpleChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(340);

    const baseData =
      filters.tipoPago === "Contado"
        ? dataConStatus.filter(isContado)
        : filters.tipoPago === "Todas"
          ? dataConStatus.filter(isCredito)
          : dataConStatus;

    const statuses =
      filters.tipoPago === "Contado" ? CONTADO_STATUS_ORDER : STATUS_ORDER;

    const data = statuses.map((status) => ({
      name: status,
      y: baseData.filter((item) => item._status === status).length,
      color:
        status === "Autorizado"
          ? colorSet.turquesa
          : status === "Rechazado"
            ? colorSet.rojo
            : status === "Pendiente de Buro"
              ? colorSet.azul
              : status === "Formalizado"
                ? colorSet.turquesa
                : status === "Cotización"
                  ? colorSet.naranja
                  : colorSet.gris,
    }));

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        type: "category",
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes",
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 6,
          borderRadiusTopRight: 6,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
              fontWeight: 700,
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          data,
        },
      ],
    };
  }, [dataConStatus, filters.tipoPago, textColor, gridColor]);

  const substatusSimpleChartOptions = useMemo(() => {
    const baseData =
      filters.tipoPago === "Todas"
        ? dataConStatus.filter(isCredito)
        : dataConStatus.filter(isCredito);

    if (!baseData.length) return getNoDataOptions(340);

    const data = SUBSTATUS_ORDER.map((sub) => ({
      name: sub,
      y: baseData.filter((item) => item._subStatus === sub).length,
    }));

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 380,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${data[this.point.index]?.name || this.category}</b><br/>Solicitudes: <b>${this.y}</b>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Subestatus",
          color: colorSet.morado,
          data: data.map((item) => ({ y: item.y, custom: { full: item.name } })),
        },
      ],
    };
  }, [dataConStatus, filters.tipoPago, textColor, gridColor]);

  const topAgenciasChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const counts = {};
    dataConStatus.forEach((item) => {
      counts[item._agencia] = (counts[item._agencia] || 0) + 1;
    });

    const data = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, y]) => ({
        name,
        y,
      }));

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.name, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return `<b>${data[this.point.index]?.name || this.category}</b><br/>Solicitudes: <b>${this.y}</b>`;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          color: colorSet.azulOscuro,
          data: data.map((item) => ({ y: item.y, custom: { full: item.name } })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const topAgenciasFormalizacionChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const map = {};
    dataConStatus.forEach((item) => {
      const key = item._agencia;
      if (!map[key]) {
        map[key] = {
          agencia: key,
          total: 0,
          formalizadas: 0,
        };
      }
      map[key].total += 1;
      if (isFormalizada(item)) map[key].formalizadas += 1;
    });

    const data = Object.values(map)
      .sort((a, b) => b.formalizadas - a.formalizadas)
      .slice(0, 10);

    return {
      chart: {
        type: "bar",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.agencia, 24)),
        labels: {
          style: {
            color: textColor,
            fontSize: "11px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Formalizaciones",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          const row = data[this.point.index];
          return `
            <b>${row?.agencia || this.category}</b><br/>
            Formalizaciones: <b>${this.y}</b><br/>
            Conversión: <b>${percentage(row?.formalizadas || 0, row?.total || 0)}%</b> (${row?.formalizadas || 0} de ${row?.total || 0})
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          dataLabels: {
            enabled: true,
            style: {
              textOutline: "none",
            },
          },
        },
      },
      series: [
        {
          name: "Formalizaciones",
          color: colorSet.naranja,
          data: data.map((item) => ({
            y: item.formalizadas,
            custom: { full: item.agencia },
          })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const tendenciaDiariaChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(340);

    const counts = {};

    dataConStatus.forEach((item) => {
      if (!item._fecha) return;
      const key = item._fecha.format("YYYY-MM-DD");
      counts[key] = (counts[key] || 0) + 1;
    });

    const keys = Object.keys(counts).sort((a, b) => a.localeCompare(b));

    return {
      chart: {
        type: "line",
        backgroundColor: chartBg,
        height: 340,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: keys.map((key) => Moment(key).format("DD MMM")),
        labels: {
          style: { color: textColor, fontSize: "10px" },
          rotation: -35,
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Solicitudes por día",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: { enabled: false },
      tooltip: {
        pointFormat: "<b>{point.y}</b> solicitudes",
      },
      plotOptions: {
        line: {
          lineWidth: 3,
          marker: {
            enabled: true,
            radius: 3,
          },
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: "Solicitudes",
          color: colorSet.morado,
          data: keys.map((key) => ({ y: counts[key], custom: { dia: key } })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  // ---------------------------------------------------------------------
  // Embudo: arranca con TODAS las solicitudes del rango (con y sin
  // seguimiento), luego las que tienen seguimiento, autorizadas/cotizadas
  // y formalizadas.
  // ---------------------------------------------------------------------
  const embudoStages = useMemo(() => {
    const total = filteredData.length;
    if (!total) return [];

    const conStatus = dataConStatus.length;
    const autorizadas = dataConStatus.filter(esAutorizadaOCotizada).length;
    const formalizadas = dataConStatus.filter(isFormalizada).length;

    const autorizadasLabel =
      filters.tipoPago === "Contado"
        ? "Cotizadas"
        : filters.tipoPago === "Crédito" || filters.tipoPago === "Arrendamiento"
          ? "Autorizadas"
          : "Autorizadas / cotizadas";

    const stages = [
      {
        key: "total",
        name: "Total de solicitudes",
        y: total,
        color: colorSet.azulOscuro,
        description:
          "Todos los clientes registrados en el periodo, tengan o no seguimiento.",
      },
      {
        key: "conStatus",
        name: "Con seguimiento",
        y: conStatus,
        color: colorSet.azul,
        description: "Clientes que ya tienen un avance registrado.",
      },
      {
        key: "autorizadas",
        name: autorizadasLabel,
        y: autorizadas,
        color: colorSet.turquesa,
        description:
          "Créditos y arrendamientos autorizados, y ventas de contado cotizadas.",
      },
      {
        key: "formalizadas",
        name: "Formalizadas",
        y: formalizadas,
        color: colorSet.naranja,
        description: "Ventas cerradas.",
      },
    ];

    return stages.map((stage, index) => ({
      ...stage,
      pctTotal: percentage(stage.y, total),
      pctPrevious: index ? percentage(stage.y, stages[index - 1].y) : "100.0",
    }));
  }, [filteredData, dataConStatus, filters.tipoPago]);

  const embudoFooter = useMemo(() => {
    const si = dataConStatus.filter(
      (item) => item._realizoPruebaManejo === "Sí",
    ).length;
    const sinSeguimiento = filteredData.length - dataConStatus.length;
    return `Sin seguimiento: ${formatNumber(sinSeguimiento)} · Prueba de manejo: ${formatNumber(si)} (${percentage(si, dataConStatus.length)}%)`;
  }, [filteredData, dataConStatus]);

  const onEmbudoClick = (stage) => {
    if (stage.key === "total") {
      // "Total" no filtra: solo quita la etapa elegida
      quitarSeleccionGrafica("etapaEmbudo");
      return;
    }
    aplicarSeleccionGrafica([
      { dim: "etapaEmbudo", value: stage.key, label: `Embudo: ${stage.name}` },
    ]);
  };

  // ---------------------------------------------------------------------
  // Histórico mensual del año + % de aprobación mensual
  // ---------------------------------------------------------------------
  // Usa rawDataAnual (año completo) con todos los filtros MENOS el rango de
  // fechas, para que siempre se vea el año entero.
  const dataAnualFiltrada = useMemo(() => {
    return rawDataAnual.filter(
      (item) =>
        pasaFiltros(item, { ignorarFecha: true }) &&
        item._fecha &&
        item._fecha.isValid() &&
        item._fecha.year() === anioHistorico,
    );
  }, [rawDataAnual, filters, seleccionGraficas, anioHistorico]);

  const MESES_CORTOS = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ];

  const historicoMensual = useMemo(() => {
    const meses = Array.from({ length: 12 }, (_, i) => ({
      mes: i,
      key: `${anioHistorico}-${String(i + 1).padStart(2, "0")}`,
      total: 0,
      conStatus: 0,
      formalizadas: 0,
      credito: 0,
      autorizadas: 0,
      rechazadas: 0,
    }));

    dataAnualFiltrada.forEach((item) => {
      const fila = meses[item._fecha.month()];
      fila.total += 1;
      if (!hasStatusReal(item)) return;
      fila.conStatus += 1;
      if (isFormalizada(item)) fila.formalizadas += 1;
      if (isCredito(item)) fila.credito += 1;
      if (isAutorizada(item)) fila.autorizadas += 1;
      if (isRechazada(item)) fila.rechazadas += 1;
    });

    // Solo hasta el mes actual si es el año en curso
    const ultimoMes =
      anioHistorico === Moment().year() ? Moment().month() : 11;
    return meses.slice(0, ultimoMes + 1);
  }, [dataAnualFiltrada, anioHistorico]);

  const mesSeleccionadoKey = useMemo(() => {
    const ini = Moment(filters.startDate, "YYYY-MM-DD", true);
    const fin = Moment(filters.endDate, "YYYY-MM-DD", true);
    if (!ini.isValid() || !fin.isValid()) return null;
    return ini.format("YYYY-MM") === fin.format("YYYY-MM")
      ? ini.format("YYYY-MM")
      : null;
  }, [filters.startDate, filters.endDate]);

  const historicoMensualChartOptions = useMemo(() => {
    if (!historicoMensual.some((m) => m.total > 0)) {
      return getNoDataOptions(360);
    }

    const categorias = historicoMensual.map((m) => MESES_CORTOS[m.mes]);
    const resaltar = (m) =>
      mesSeleccionadoKey && m.key === mesSeleccionadoKey
        ? { borderColor: textColor, borderWidth: 2 }
        : {};

    return {
      chart: { type: "column", backgroundColor: chartBg, height: 360 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: categorias,
        labels: { style: { color: textColor, fontWeight: 600 } },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        title: { text: "Solicitudes", style: { color: textColor } },
        labels: { style: { color: textColor } },
        gridLineColor: gridColor,
      },
      legend: { itemStyle: { color: textColor, fontSize: "11px" } },
      tooltip: {
        shared: true,
        formatter: function () {
          const m = historicoMensual[this.points[0].point.index];
          return `
            <b>${MESES_CORTOS[m.mes]} ${anioHistorico}</b><br/>
            Total de solicitudes: <b>${formatNumber(m.total)}</b><br/>
            Con seguimiento: <b>${formatNumber(m.conStatus)}</b> (${percentage(m.conStatus, m.total)}%)<br/>
            Formalizadas: <b>${formatNumber(m.formalizadas)}</b> (${percentage(m.formalizadas, m.conStatus)}% de las que tienen seguimiento)
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
          groupPadding: 0.12,
          dataLabels: {
            enabled: true,
            style: { textOutline: "none", fontSize: "10px", color: textColor },
            formatter: function () {
              return this.y ? this.y : null;
            },
          },
        },
      },
      series: [
        {
          name: "Total de solicitudes",
          color: colorSet.azulOscuro,
          data: historicoMensual.map((m) => ({
            y: m.total,
            custom: { mes: m.key },
            ...resaltar(m),
          })),
        },
        {
          name: "Con seguimiento",
          color: colorSet.azul,
          data: historicoMensual.map((m) => ({
            y: m.conStatus,
            custom: { mes: m.key },
          })),
        },
        {
          name: "Formalizadas",
          color: colorSet.naranja,
          data: historicoMensual.map((m) => ({
            y: m.formalizadas,
            custom: { mes: m.key },
          })),
        },
      ],
    };
  }, [historicoMensual, mesSeleccionadoKey, textColor, gridColor]);

  const aprobacionMensualChartOptions = useMemo(() => {
    if (!historicoMensual.some((m) => m.credito > 0)) {
      return getNoDataOptions(360);
    }

    return {
      chart: { backgroundColor: chartBg, height: 360 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: historicoMensual.map((m) => MESES_CORTOS[m.mes]),
        labels: { style: { color: textColor, fontWeight: 600 } },
        lineColor: gridColor,
      },
      yAxis: [
        {
          min: 0,
          allowDecimals: false,
          title: { text: "Solicitudes de crédito", style: { color: textColor } },
          labels: { style: { color: textColor } },
          gridLineColor: gridColor,
        },
        {
          min: 0,
          max: 100,
          opposite: true,
          title: { text: "% Aprobación", style: { color: textColor } },
          labels: { format: "{value}%", style: { color: textColor } },
          gridLineWidth: 0,
        },
      ],
      legend: { itemStyle: { color: textColor, fontSize: "11px" } },
      tooltip: {
        shared: true,
        formatter: function () {
          const m = historicoMensual[this.points[0].point.index];
          return `
            <b>${MESES_CORTOS[m.mes]} ${anioHistorico}</b><br/>
            % Aprobación: <b>${percentage(m.autorizadas, m.credito)}%</b><br/>
            Autorizadas: <b>${m.autorizadas}</b> · Rechazadas: <b>${m.rechazadas}</b><br/>
            Solicitudes de crédito: <b>${m.credito}</b>
          `;
        },
      },
      plotOptions: {
        column: {
          stacking: "normal",
          borderRadiusTopLeft: 3,
          borderRadiusTopRight: 3,
        },
      },
      series: [
        {
          type: "column",
          name: "Autorizadas",
          color: colorSet.turquesa,
          data: historicoMensual.map((m) => ({
            y: m.autorizadas,
            custom: { mes: m.key },
          })),
        },
        {
          type: "column",
          name: "Rechazadas",
          color: colorSet.rojo,
          data: historicoMensual.map((m) => ({
            y: m.rechazadas,
            custom: { mes: m.key },
          })),
        },
        {
          type: "column",
          name: "Otras (en trámite)",
          color: colorSet.gris,
          data: historicoMensual.map((m) => ({
            y: Math.max(m.credito - m.autorizadas - m.rechazadas, 0),
            custom: { mes: m.key },
          })),
        },
        {
          type: "spline",
          name: "% Aprobación",
          yAxis: 1,
          color: colorSet.azulOscuro,
          lineWidth: 3,
          marker: { enabled: true, radius: 4 },
          dataLabels: {
            enabled: true,
            format: "{y}%",
            style: { textOutline: "none", fontWeight: 700, color: textColor },
          },
          data: historicoMensual.map((m) => ({
            y: m.credito
              ? Number(((m.autorizadas / m.credito) * 100).toFixed(1))
              : null,
            custom: { mes: m.key },
          })),
        },
      ],
    };
  }, [historicoMensual, textColor, gridColor]);

  // ---------------------------------------------------------------------
  // Solicitudes vs. afluencia (Seekop)
  // ---------------------------------------------------------------------
  // Afluencia normalizada a { agencia, fecha, cantidad, fila matriz }. Hoy
  // sale de Babilonia (cada cliente registrado = 1 visita); con Seekop sale
  // del servicio. Se le aplican los mismos filtros de organización y fecha.
  const afluenciaPorAgencia = useMemo(() => {
    const start = Moment(filters.startDate, "YYYY-MM-DD", true).startOf("day");
    const end = Moment(filters.endDate, "YYYY-MM-DD", true).endOf("day");

    const pasaOrganizacion = (agencia, matriz) => {
      const grupo = matriz?.grupo || SIN_ASIGNAR;
      const direccion = matriz?.direccion || SIN_ASIGNAR;
      const razonSocial = matriz?.razonSocial || SIN_ASIGNAR;
      if (filters.agencia.length && !filters.agencia.includes(agencia)) {
        return false;
      }
      if (filters.grupo !== "Todas" && grupo !== filters.grupo) return false;
      if (filters.direccion !== "Todas" && direccion !== filters.direccion) {
        return false;
      }
      if (
        filters.razonSocial !== "Todas" &&
        razonSocial !== filters.razonSocial
      ) {
        return false;
      }
      return true;
    };

    const visitas = {};

    if (FUENTE_AFLUENCIA === "seekop") {
      afluenciaSeekop.forEach((row) => {
        const matriz = buscarAgenciaEnMatriz(row?.ag_id, row?.agencia);
        const agencia = normalizeAgencia(matriz?.agencia || row?.agencia);
        const fecha = Moment(row?.fecha, ["YYYY-MM-DD", Moment.ISO_8601]);
        if (start.isValid() && end.isValid() && fecha.isValid()) {
          if (!fecha.isBetween(start, end, undefined, "[]")) return;
        }
        if (!pasaOrganizacion(agencia, matriz)) return;
        visitas[agencia] = (visitas[agencia] || 0) + Number(row?.cantidad || 0);
      });
    } else {
      // Con lo que tenemos: todos los clientes registrados (filtros de
      // organización y fecha; no se aplican tipo de pago ni selecciones de
      // gráficas porque la afluencia no tiene esos datos).
      rawData
        .filter((item) =>
          pasaFiltros(item, {
            excluir: "tipoPago",
            sinSeleccionGraficas: true,
          }),
        )
        .forEach((item) => {
          visitas[item._agencia] = (visitas[item._agencia] || 0) + 1;
        });
    }

    // Solicitudes = clientes con seguimiento (con todos los filtros)
    const solicitudes = {};
    dataConStatus.forEach((item) => {
      solicitudes[item._agencia] = (solicitudes[item._agencia] || 0) + 1;
    });

    const agencias = Array.from(
      new Set([...Object.keys(visitas), ...Object.keys(solicitudes)]),
    );

    const rows = agencias
      .map((agencia) => {
        const afluencia = visitas[agencia] || 0;
        const sol = solicitudes[agencia] || 0;
        return {
          agencia,
          afluencia,
          solicitudes: sol,
          pctConversion: afluencia
            ? Number(((sol / afluencia) * 100).toFixed(1))
            : null,
        };
      })
      .sort((a, b) => b.afluencia - a.afluencia);

    const totalAfluencia = rows.reduce((acc, r) => acc + r.afluencia, 0);
    const totalSolicitudes = rows.reduce((acc, r) => acc + r.solicitudes, 0);

    return { rows, totalAfluencia, totalSolicitudes };
  }, [rawData, afluenciaSeekop, dataConStatus, filters, seleccionGraficas]);

  const afluenciaChartOptions = useMemo(() => {
    const { rows } = afluenciaPorAgencia;
    if (!rows.length) return getNoDataOptions(380);

    const top = rows.slice(0, 12);

    return {
      chart: { backgroundColor: chartBg, height: 380 },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: top.map((r) => truncateText(r.agencia, 18)),
        labels: {
          rotation: -25,
          style: { color: textColor, fontSize: "10px" },
        },
        lineColor: gridColor,
      },
      yAxis: [
        {
          min: 0,
          allowDecimals: false,
          title: { text: "Personas", style: { color: textColor } },
          labels: { style: { color: textColor } },
          gridLineColor: gridColor,
        },
        {
          min: 0,
          max: 100,
          opposite: true,
          title: { text: "% Conversión", style: { color: textColor } },
          labels: { format: "{value}%", style: { color: textColor } },
          gridLineWidth: 0,
        },
      ],
      legend: { itemStyle: { color: textColor, fontSize: "11px" } },
      tooltip: {
        shared: true,
        formatter: function () {
          const r = top[this.points[0].point.index];
          return `
            <b>${r.agencia}</b><br/>
            Afluencia: <b>${formatNumber(r.afluencia)}</b><br/>
            Solicitudes con seguimiento: <b>${formatNumber(r.solicitudes)}</b><br/>
            Conversión: <b>${r.pctConversion ?? 0}%</b>
          `;
        },
      },
      plotOptions: {
        column: { borderRadiusTopLeft: 4, borderRadiusTopRight: 4 },
      },
      series: [
        {
          type: "column",
          name: "Afluencia",
          color: colorSet.celeste,
          data: top.map((r) => ({ y: r.afluencia, custom: { full: r.agencia } })),
        },
        {
          type: "column",
          name: "Solicitudes con seguimiento",
          color: colorSet.azulOscuro,
          data: top.map((r) => ({
            y: r.solicitudes,
            custom: { full: r.agencia },
          })),
        },
        {
          type: "spline",
          name: "% Conversión",
          yAxis: 1,
          color: colorSet.naranja,
          lineWidth: 3,
          marker: { enabled: true, radius: 4 },
          dataLabels: {
            enabled: true,
            format: "{y}%",
            style: { textOutline: "none", fontWeight: 700, color: textColor },
          },
          data: top.map((r) => ({
            y: r.pctConversion,
            custom: { full: r.agencia },
          })),
        },
      ],
    };
  }, [afluenciaPorAgencia, textColor, gridColor]);

  // ---------------------------------------------------------------------
  // Razón social (tabla)
  // ---------------------------------------------------------------------
  const razonSocialRows = useMemo(() => {
    const map = {};
    dataConStatus.forEach((item) => {
      const key = item._razonSocial;
      if (!map[key]) {
        map[key] = {
          razonSocial: key,
          grupo: item._grupo,
          direccion: item._direccion,
          total: 0,
          creditos: 0,
          autorizadas: 0,
          formalizaciones: 0,
        };
      }
      map[key].total += 1;
      if (isCredito(item)) map[key].creditos += 1;
      if (isAutorizada(item)) map[key].autorizadas += 1;
      if (isFormalizada(item)) map[key].formalizaciones += 1;
    });

    return Object.values(map)
      .map((row) => ({
        ...row,
        pctAprobacion: percentage(row.autorizadas, row.creditos),
      }))
      .sort((a, b) => b.total - a.total);
  }, [dataConStatus]);

  // ---------------------------------------------------------------------
  // Panel: desglose por asesor y clientes del asesor
  // ---------------------------------------------------------------------
  // Usa filteredData (incluye clientes sin seguimiento) para que el asesor
  // vea también a quién le falta dar seguimiento.
  const desgloseAsesores = useMemo(() => {
    const map = {};
    filteredData.forEach((item) => {
      const asesor = item._asesor;
      if (!map[asesor]) {
        map[asesor] = {
          asesor,
          agenciasSet: new Set(),
          total: 0,
          conStatus: 0,
          creditos: 0,
          autorizadas: 0,
          formalizadas: 0,
        };
      }
      const row = map[asesor];
      row.agenciasSet.add(item._agencia);
      row.total += 1;
      if (hasStatusReal(item)) row.conStatus += 1;
      if (isCredito(item) && hasStatusReal(item)) row.creditos += 1;
      if (isAutorizada(item)) row.autorizadas += 1;
      if (isFormalizada(item)) row.formalizadas += 1;
    });

    return Object.values(map)
      .map(({ agenciasSet, ...row }) => ({
        ...row,
        agencias: Array.from(agenciasSet).join(", "),
        pctAprobacion: percentage(row.autorizadas, row.creditos),
      }))
      .sort((a, b) => b.total - a.total);
  }, [filteredData]);

  const clientesAsesor = useMemo(() => {
    if (!asesorPanel) return [];

    return filteredData
      .filter((item) => item._asesor === asesorPanel)
      .sort((a, b) => (b._fecha?.valueOf() || 0) - (a._fecha?.valueOf() || 0))
      .map((item) => {
        const statusTexto = !hasStatusReal(item)
          ? "Sin seguimiento"
          : isCredito(item) && item._subStatus !== "Sin substatus"
            ? `${item._status} · ${item._subStatus}`
            : item._status;

        return {
          id: item.id,
          folio: normalizeText(item.folio_venta),
          nombreCompleto:
            [item.nombre, item.apellido_paterno, item.apellido_materno]
              .map(normalizeText)
              .filter(Boolean)
              .join(" ") || "(Sin nombre)",
          telefono: normalizeText(item.telefono),
          correo: normalizeText(item.correo_electronico),
          fecha: item._fecha ? item._fecha.format("DD/MM/YYYY") : "",
          agencia: item._agencia,
          tipoPago: item._tipoPago,
          financiera:
            item._financiera !== "(En blanco)" ? item._financiera : "",
          statusTexto,
          vehiculo: [
            item.marca_vehiculo,
            item.modelo_vehiculo,
            item.version_vehiculo,
            item.ano_vehiculo,
          ]
            .map(normalizeText)
            .filter(Boolean)
            .join(" "),
          fuente: item._fuente,
          pruebaManejo: item._realizoPruebaManejo,
          // Solicitud tardía: fecha muestra el día al que corresponde y
          // fechaCapturaTardia el día real en que se capturó
          esTardia: Boolean(item._esTardia),
          fechaCapturaTardia: (() => {
            if (!item._esTardia || !item.fecha_registro_tardia) return "";
            const m = Moment(
              item.fecha_registro_tardia,
              ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", Moment.ISO_8601],
              true,
            );
            return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
          })(),
          // Lo que el cliente seleccionó en su plan
          planes: (item._selectedPlanTitles || [])
            .map((plan) => normalizePlanTitle(plan))
            .filter(Boolean),
          ...(() => {
            const accesorios = [];
            const garantias = [];
            (item._selectedAccessories || []).forEach((acc) => {
              const tipo = normalizeText(acc?.type || "").toLowerCase();
              const monto = getAccessoryAmount(acc);
              const fila = {
                titulo: normalizeAccessoryTitle(acc?.title) || "(Sin nombre)",
                monto: monto ? formatCurrency(monto) : "",
              };
              if (tipo === "garantias") garantias.push(fila);
              else accesorios.push(fila);
            });
            return { accesorios, garantias };
          })(),
        };
      });
  }, [filteredData, asesorPanel]);

  const abrirClientesAsesor = (asesor) => {
    setAsesorPanel(asesor);
    setPanelAbierto(true);
  };

  // Chips de la barra "Filtros activos" (cada uno con su forma de quitarse)
  const chipsFiltrosActivos = (() => {
    const chips = [];
    if (!isRangoDefault) {
      chips.push({
        key: "fecha",
        label: `Fecha: ${selectedDateRangeText}`,
        quitar: clearDateFilter,
      });
    }
    filters.agencia.forEach((ag) => {
      chips.push({
        key: `agencia-${ag}`,
        label: `Agencia: ${ag}`,
        quitar: () =>
          setFilters((prev) => ({
            ...prev,
            agencia: prev.agencia.filter((a) => a !== ag),
          })),
      });
    });
    [
      ["grupo", "Grupo"],
      ["direccion", "Dirección"],
      ["razonSocial", "Razón social"],
      ["departamento", "Departamento"],
      ["tipoPago", "Tipo de pago"],
    ].forEach(([key, nombre]) => {
      if (filters[key] !== "Todas") {
        chips.push({
          key,
          label: `${nombre}: ${filters[key]}`,
          quitar: () => setFilters((prev) => ({ ...prev, [key]: "Todas" })),
        });
      }
    });
    Object.entries(seleccionGraficas).forEach(([dim, sel]) => {
      chips.push({
        key: `sel-${dim}`,
        label: sel.label,
        deGrafica: true,
        quitar: () => quitarSeleccionGrafica(dim),
      });
    });
    return chips;
  })();

  // Etiquetas de lo que está filtrando el panel / la barra de chips
  const chipsActivos = useMemo(() => {
    const chips = [];
    if (filters.agencia.length) chips.push(`Agencia: ${agenciaFilterLabel}`);
    if (filters.grupo !== "Todas") chips.push(`Grupo: ${filters.grupo}`);
    if (filters.direccion !== "Todas") {
      chips.push(`Dirección: ${filters.direccion}`);
    }
    if (filters.razonSocial !== "Todas") {
      chips.push(`Razón social: ${filters.razonSocial}`);
    }
    if (filters.departamento !== "Todas") {
      chips.push(`Departamento: ${filters.departamento}`);
    }
    if (filters.tipoPago !== "Todas") chips.push(`Pago: ${filters.tipoPago}`);
    Object.values(seleccionGraficas).forEach((sel) => chips.push(sel.label));
    chips.push(`Rango: ${selectedDateRangeText}`);
    return chips;
  }, [filters, seleccionGraficas, agenciaFilterLabel, selectedDateRangeText]);

  const conversionAgenciaChartOptions = useMemo(() => {
    if (!dataConStatus.length) return getNoDataOptions(400);

    const map = {};
    dataConStatus.forEach((item) => {
      const key = item._agencia;
      if (!map[key]) {
        map[key] = {
          agencia: key,
          autorizadas: 0,
          formalizadas: 0,
          total: 0,
        };
      }
      map[key].total += 1;
      if (isAutorizada(item)) map[key].autorizadas += 1;
      if (isFormalizada(item)) map[key].formalizadas += 1;
    });

    const data = Object.values(map)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);

    return {
      chart: {
        type: "column",
        backgroundColor: chartBg,
        height: 400,
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: data.map((item) => truncateText(item.agencia, 18)),
        labels: {
          rotation: -20,
          style: {
            color: textColor,
            fontSize: "10px",
          },
        },
        lineColor: gridColor,
      },
      yAxis: {
        min: 0,
        title: {
          text: "Cantidad",
          style: { color: textColor },
        },
        labels: {
          style: { color: textColor },
        },
        gridLineColor: gridColor,
        allowDecimals: false,
      },
      legend: {
        itemStyle: {
          color: textColor,
          fontSize: "11px",
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const idx = this.points?.[0]?.point?.index ?? 0;
          const row = data[idx];
          if (!row) return false;

          return `
            <b>${row.agencia}</b><br/>
            Total: <b>${row.total}</b><br/>
            <span style="color:${colorSet.turquesa}">\u25CF</span> Autorizadas: <b>${row.autorizadas}</b> (${percentage(row.autorizadas, row.total)}%)<br/>
            <span style="color:${colorSet.naranja}">\u25CF</span> Formalizadas: <b>${row.formalizadas}</b> (${percentage(row.formalizadas, row.total)}%)
          `;
        },
      },
      plotOptions: {
        column: {
          borderRadiusTopLeft: 4,
          borderRadiusTopRight: 4,
        },
      },
      series: [
        {
          name: "Autorizadas",
          color: colorSet.turquesa,
          data: data.map((item) => ({
            y: item.autorizadas,
            custom: { full: item.agencia, resultado: "autorizada" },
          })),
        },
        {
          name: "Formalizadas",
          color: colorSet.naranja,
          data: data.map((item) => ({
            y: item.formalizadas,
            custom: { full: item.agencia, resultado: "formalizada" },
          })),
        },
      ],
    };
  }, [dataConStatus, textColor, gridColor]);

  const StatCard = ({
    value,
    label,
    accent = "#d95a6f",
    icon = "fa-solid fa-chart-line",
    detail = "",
    info = "",
  }) => {
    const isRangeDetail =
      typeof detail === "string" &&
      detail.trim().toLowerCase().startsWith("rango:");

    return (
      <div className="col-12 col-sm-6 col-lg">
        <div
          className="h-100 position-relative overflow-hidden"
          style={{
            background: isDarkMode ? "#111827" : "#ffffff",
            borderRadius: "22px",
            border: isDarkMode ? "1px solid #2a3441" : "1px solid #ececec",
            boxShadow: isDarkMode
              ? "0 10px 24px rgba(0,0,0,0.28)"
              : "0 10px 24px rgba(15, 23, 42, 0.08)",
            padding: "20px 22px 18px",
            minHeight: "138px",
            transition: "all .25s ease",
            cursor: "default",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "5px",
              background: accent,
            }}
          />

          <div className="d-flex align-items-start justify-content-between">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: "2.7rem",
                  fontWeight: 800,
                  color: textColor,
                  lineHeight: 1,
                  marginBottom: "10px",
                }}
              >
                {value}
              </div>

              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: mutedColor,
                  lineHeight: 1.3,
                  marginBottom: detail ? "8px" : 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {label}
                <InfoTooltip text={info} />
              </div>

              {detail ? (
                isRangeDetail ? (
                  <div
                    style={{
                      marginTop: "2px",
                      background: isDarkMode
                        ? "rgba(47,128,237,0.16)"
                        : "rgba(47,128,237,0.08)",
                      borderLeft: `3px solid ${accent}`,
                      padding: "6px 10px",
                      borderRadius: "6px",
                      display: "inline-block",
                      fontSize: "13px",
                      lineHeight: 1.4,
                      whiteSpace: "pre-line",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: isDarkMode ? "#dbeafe" : "#1f3c88",
                      }}
                    >
                      Rango:
                    </span>{" "}
                    <span
                      style={{
                        fontWeight: 700,
                        color: isDarkMode ? "#ffffff" : "#111827",
                      }}
                    >
                      {detail.replace(/^Rango:\s*/i, "")}
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: softTextColor,
                      lineHeight: 1.35,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {detail}
                  </div>
                )
              ) : null}
            </div>

            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "14px",
                background: isDarkMode ? "rgba(255,255,255,0.06)" : "#f4f6f8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                flexShrink: 0,
                marginLeft: "10px",
              }}
            >
              <i className={icon} style={{ fontSize: "20px" }}></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Opciones de cada gráfica con el clic ya conectado
  const etiquetaResultado = {
    autorizada: "Autorizadas",
    formalizada: "Formalizadas",
    rechazada: "Rechazadas",
  };

  // useMemo para no recrear las gráficas en cada render (immutable={true})
  const drill = useMemo(() => ({
    statusSubstatus: conDrill(statusSubstatusChartOptions, (p) => ({
      dim: "statusSubstatusCredito",
      value: { status: p.category, subStatus: p.series.name },
      label: `Crédito: ${p.category} · ${p.series.name}`,
    })),
    contadoStatus: conDrill(contadoStatusChartOptions, (p) => ({
      dim: "statusContado",
      value: p.name,
      label: `Contado: ${p.name}`,
    })),
    arrendamientoStatus: conDrill(arrendamientoStatusChartOptions, (p) => ({
      dim: "statusArrendamiento",
      value: p.name,
      label: `Arrendamiento: ${p.name}`,
    })),
    fuente: conDrill(fuenteChartOptions, (p) => ({
      dim: "fuente",
      value: valorConOtros(p),
      label: `Fuente: ${p.name}`,
    })),
    financiera: conDrill(financieraChartOptions, (p) => ({
      dim: "financiera",
      value: valorConOtros(p),
      label: `Financiera: ${p.name}`,
    })),
    tipoPago: conDrill(tipoPagoComparacionChartOptions, (p) => ({
      dim: "tipoPago",
      value: customDe(p).full,
    })),
    ventaBabilonia: conDrill(ventaBabiloniaChartOptions, (p) => ({
      dim: "ventaBabilonia",
      value: customDe(p).valor,
      label: `Venta en Babilonia: ${p.name}`,
    })),
    ventaNr: conDrill(ventaNrChartOptions, (p) => ({
      dim: "ventaNr",
      value: customDe(p).valor,
      label: p.name,
    })),
    solicitudesTardias: conDrill(solicitudesTardiasChartOptions, (p) => [
      { dim: "solicitudTardia", value: true, label: "Solicitudes tardías" },
      { dim: "agencia", value: p.category },
    ]),
    pruebaManejo: conDrill(pruebaManejoChartOptions, (p) => ({
      dim: "pruebaManejo",
      value: customDe(p).valor,
      label: `Prueba de manejo: ${p.name}`,
    })),
    planes: conDrill(planesSeleccionadosChartOptions, (p) => ({
      dim: "plan",
      value: p.name,
      label: `Plan: ${p.name}`,
    })),
    topAccesorios: conDrill(topAccesoriosGarantiasChartOptions, (p) => ({
      dim: "accesorio",
      value: customDe(p).full,
      label: `Accesorio: ${customDe(p).full}`,
    })),
    agenciaDepartamento: conDrill(agenciaDepartamentoChartOptions, (p) => [
      { dim: "agencia", value: p.category },
      { dim: "departamento", value: p.series.name },
    ]),
    statusSimple: conDrill(statusSimpleChartOptions, (p) => {
      const dim =
        filters.tipoPago === "Contado"
          ? "statusContado"
          : filters.tipoPago === "Arrendamiento"
            ? "statusArrendamiento"
            : "statusCredito";
      return { dim, value: p.name, label: `Status: ${p.name}` };
    }),
    substatus: conDrill(substatusSimpleChartOptions, (p) => ({
      dim: "substatusCredito",
      value: customDe(p).full,
      label: `Substatus: ${customDe(p).full}`,
    })),
    tendencia: conDrill(tendenciaDiariaChartOptions, (p) => ({
      dim: "dia",
      value: customDe(p).dia,
      label: `Día: ${Moment(customDe(p).dia).format("DD/MM/YYYY")}`,
    })),
    topAgencias: conDrill(topAgenciasChartOptions, (p) => ({
      dim: "agencia",
      value: customDe(p).full,
    })),
    topAgenciasFormalizacion: conDrill(
      topAgenciasFormalizacionChartOptions,
      (p) => [
        { dim: "agencia", value: customDe(p).full },
        { dim: "resultado", value: "formalizada", label: "Formalizadas" },
      ],
    ),
    conversionAgencia: conDrill(conversionAgenciaChartOptions, (p) => [
      { dim: "agencia", value: customDe(p).full },
      {
        dim: "resultado",
        value: customDe(p).resultado,
        label: etiquetaResultado[customDe(p).resultado],
      },
    ]),
    formalizacionesFinanciera: conDrill(
      formalizacionesFinancieraChartOptions,
      (p) => [
        {
          dim: "financiera",
          value: valorConOtros(p),
          label: `Financiera: ${p.name}`,
        },
        { dim: "resultado", value: "formalizada", label: "Formalizadas" },
      ],
    ),
    historicoMensual: conDrill(historicoMensualChartOptions, (p) => ({
      dim: "mes",
      value: customDe(p).mes,
    })),
    aprobacionMensual: conDrill(aprobacionMensualChartOptions, (p) => ({
      dim: "mes",
      value: customDe(p).mes,
    })),
    afluencia: conDrill(afluenciaChartOptions, (p) => ({
      dim: "agencia",
      value: customDe(p).full,
    })),
  }), [
    statusSubstatusChartOptions,
    contadoStatusChartOptions,
    arrendamientoStatusChartOptions,
    fuenteChartOptions,
    financieraChartOptions,
    tipoPagoComparacionChartOptions,
    ventaBabiloniaChartOptions,
    ventaNrChartOptions,
    solicitudesTardiasChartOptions,
    pruebaManejoChartOptions,
    planesSeleccionadosChartOptions,
    topAccesoriosGarantiasChartOptions,
    agenciaDepartamentoChartOptions,
    statusSimpleChartOptions,
    substatusSimpleChartOptions,
    tendenciaDiariaChartOptions,
    topAgenciasChartOptions,
    topAgenciasFormalizacionChartOptions,
    conversionAgenciaChartOptions,
    formalizacionesFinancieraChartOptions,
    historicoMensualChartOptions,
    aprobacionMensualChartOptions,
    afluenciaChartOptions,
    filters.tipoPago,
  ]);

  const opcionesAnio = [0, 1, 2].map((n) => Moment().year() - n);

  const pctConversionAfluencia = percentage(
    afluenciaPorAgencia.totalSolicitudes,
    afluenciaPorAgencia.totalAfluencia,
  );

  const cardStyle = {
    boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
    background: cardBg,
    borderRadius: "18px",
  };

  return (
    <div>
      {loading ? (
        <div className="loader-data">
          <Circles height="80" width="80" color="#4fa94d" />
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
            <div>
              <h1 className="page-header m-0">CONTROL DE SOLICITUDES AL DÍA</h1>
              <div
                style={{
                  color: mutedColor,
                  fontSize: "13px",
                  marginTop: "4px",
                }}
              >
                Vista general de solicitudes, conversión y desempeño
              </div>
            </div>

            <div
              style={{
                background: isDarkMode ? "#1f2937" : "#fff",
                borderRadius: "12px",
                padding: "12px 20px",
                border: isDarkMode ? "1px solid #374151" : "1px solid #d9d9d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "240px",
                height: "90px",
              }}
            >
              <img
                src="/assets/img/gocar_negro.png"
                alt="GOCAR"
                style={{
                  maxWidth: "190px",
                  maxHeight: "62px",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>

          <div className="row g-3 mb-4">
            <StatCard
              value={formatNumber(metrics.total)}
              label="Total clientes babilonia"
              info={INFO_INDICADORES.totalClientes}
              detail={`Rango: ${selectedDateRangeText}`}
              accent={colorSet.azul}
              icon="fa-solid fa-file-lines"
            />

            <StatCard
              value={formatNumber(metrics.conStatus)}
              label="Solicitudes con status"
              info={INFO_INDICADORES.conStatus}
              detail={`Faltan por asignar: ${formatNumber(metrics.pendientesStatus)}\n${metrics.pctSolicitudesTotales}% del total clientes\nCrédito: ${formatNumber(metrics.totalCredito)} · Contado: ${formatNumber(metrics.totalContado)} · Arrendamiento: ${formatNumber(metrics.totalArrendamiento)}`}
              accent={colorSet.turquesa}
              icon="fa-solid fa-circle-check"
            />

            {shouldShowCreditOnlyBlocks && (
              <>
                <StatCard
                  value={`${metrics.pctAprobacion}%`}
                  label="% De Aprobación"
                  info={INFO_INDICADORES.pctAprobacion}
                  detail={`Autorizadas: ${formatNumber(metrics.autorizadas)} de ${formatNumber(metrics.baseTasa)}\nUniverso: ${formatNumber(metrics.totalCredito)} · Pendientes de buró: ${formatNumber(metrics.pendientesBuro)}`}
                  accent={colorSet.turquesa}
                  icon="fa-solid fa-circle-check"
                />

                <StatCard
                  value={`${metrics.pctRechazo}%`}
                  label="% Rechazadas"
                  info={INFO_INDICADORES.pctRechazo}
                  detail={`Rechazadas: ${formatNumber(metrics.rechazadas)} de ${formatNumber(metrics.baseTasa)}\nUniverso: ${formatNumber(metrics.totalCredito)} · Pendientes de buró: ${formatNumber(metrics.pendientesBuro)}`}
                  accent={colorSet.rojo}
                  icon="fa-solid fa-circle-xmark"
                />

                <StatCard
                  value={formatNumber(metrics.formalizadas)}
                  label="Formalizadas"
                  info={INFO_INDICADORES.formalizadas}
                  detail="Según su fecha de formalización (puede incluir solicitudes registradas en un mes distinto al filtrado)"
                  accent={colorSet.naranja}
                  icon="fa-solid fa-handshake"
                />
              </>
            )}
          </div>

          <div className="row g-3 mb-3 align-items-end">
            <div className="col-12 col-md-8 col-xl-4">
              <label className="form-label fw-semibold black-text">
                Fecha
                {/* El aviso va en la etiqueta y no debajo del campo para que
                    el selector quede alineado con los demás filtros */}
                {isRangoDefault ? (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 400,
                      color: mutedColor,
                      marginLeft: "6px",
                    }}
                  >
                    ({ETIQUETA_RANGO_DEFAULT})
                  </span>
                ) : null}
              </label>

              <div className="d-flex align-items-stretch gap-2">
                <div className="flex-grow-1">
                  <DateRangePicker
                    key={datePickerResetKey}
                    ref={datePickerRef}
                    startDate={
                      filters.startDate
                        ? Moment(filters.startDate, "YYYY-MM-DD")
                        : Moment().startOf("month")
                    }
                    endDate={
                      filters.endDate
                        ? Moment(filters.endDate, "YYYY-MM-DD")
                        : Moment()
                    }
                    ranges={dateRangePresets}
                    locale={{
                      format: "YYYY-MM-DD",
                      applyLabel: "Aplicar",
                      cancelLabel: "Cancelar",
                      customRangeLabel: "Rango personalizado",
                    }}
                    onApply={handleDateApplyEvent}
                    onCancel={clearDateFilter}
                  >
                    <button
                      type="button"
                      className="form-control text-start"
                      style={{ width: "100%" }}
                    >
                      {filters.startDate && filters.endDate
                        ? `${filters.startDate} | ${filters.endDate}`
                        : "Selecciona un rango"}
                    </button>
                  </DateRangePicker>
                </div>

                {!isRangoDefault ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                    onClick={clearDateFilter}
                    title={`Regresar al ${ETIQUETA_RANGO_DEFAULT}`}
                    style={{
                      minWidth: "44px",
                      width: "44px",
                      padding: "0",
                      flexShrink: 0,
                    }}
                  >
                    <IconoX />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Grupo
              </label>
              <select
                className="form-select"
                value={filters.grupo}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    grupo: e.target.value,
                  }))
                }
              >
                {selectOptions.grupo.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Dirección
              </label>
              <select
                className="form-select"
                value={filters.direccion}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    direccion: e.target.value,
                  }))
                }
              >
                {selectOptions.direccion.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Razón Social
              </label>
              <select
                className="form-select"
                value={filters.razonSocial}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    razonSocial: e.target.value,
                  }))
                }
              >
                {selectOptions.razonSocial.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Agencia
              </label>
              <div ref={agenciaDropdownRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  className="form-select text-start d-flex justify-content-between align-items-center"
                  onClick={() => setAgenciaDropdownOpen((prev) => !prev)}
                  style={{
                    backgroundColor: cardBg,
                    color: textColor,
                    border: `1px solid ${gridColor}`,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {agenciaFilterLabel}
                  </span>
                </button>

                {agenciaDropdownOpen ? (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      width: "max-content",
                      minWidth: "100%",
                      maxWidth: "280px",
                      maxHeight: "280px",
                      overflowY: "auto",
                      backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                      border: `1px solid ${gridColor}`,
                      borderRadius: "8px",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
                      padding: "8px",
                      zIndex: 1050,
                    }}
                  >
                    <label
                      htmlFor="agencia-todas"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px 6px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        color: textColor,
                        fontWeight: 600,
                      }}
                    >
                      <input
                        type="checkbox"
                        id="agencia-todas"
                        checked={isAgenciaAllSelected}
                        onChange={() =>
                          setFilters((prev) => ({ ...prev, agencia: [] }))
                        }
                        style={{
                          accentColor: colorSet.azul,
                          cursor: "pointer",
                        }}
                      />
                      Todas
                    </label>

                    <hr
                      style={{
                        margin: "6px 0",
                        borderColor: gridColor,
                        opacity: 1,
                      }}
                    />

                    {selectOptions.agencia.length > 0 ? (
                      selectOptions.agencia.map((option) => (
                        <label
                          key={option}
                          htmlFor={`agencia-${option}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "6px 6px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: textColor,
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`agencia-${option}`}
                            checked={filters.agencia.includes(option)}
                            onChange={() => toggleAgenciaFilter(option)}
                            style={{
                              accentColor: colorSet.azul,
                              cursor: "pointer",
                            }}
                          />
                          {option}
                        </label>
                      ))
                    ) : (
                      <div
                        style={{
                          color: mutedColor,
                          fontSize: "0.85rem",
                          padding: "6px",
                        }}
                      >
                        Sin agencias
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Departamento
              </label>
              <select
                className="form-select"
                value={filters.departamento}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    departamento: e.target.value,
                  }))
                }
              >
                {selectOptions.departamento.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-xl-2">
              <label className="form-label fw-semibold black-text">
                Tipo de pago
              </label>
              <select
                className="form-select"
                value={filters.tipoPago}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    tipoPago: e.target.value,
                  }))
                }
              >
                <option value="Todas">Todas</option>
                <option value="Crédito">Crédito</option>
                <option value="Contado">Contado</option>
                <option value="Arrendamiento">Arrendamiento</option>
              </select>
            </div>

            <div className="col-12 col-md-4 col-xl-2 d-grid">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={clearAllFilters}
                title="Limpiar todos los filtros"
              >
                Limpiar
              </button>
            </div>
          </div>

          {/* ---------------------------------------------------------
              Filtros activos: TODO lo que está filtrando el tablero
              (filtros de arriba + lo seleccionado en gráficas). Se queda
              fijo arriba al hacer scroll para que siempre se vea.
             --------------------------------------------------------- */}
          <div
            style={{
              position: "sticky",
              top: "var(--bs-app-header-height, 56px)",
              zIndex: 20,
              background: hayFiltrosActivos
                ? isDarkMode
                  ? "#1e3a5f"
                  : "#e8f1ff"
                : isDarkMode
                  ? "#1f2937"
                  : "#f4f6f8",
              border: `1px solid ${hayFiltrosActivos ? colorSet.azul : gridColor}`,
              borderRadius: 12,
              padding: "8px 12px",
              marginBottom: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span
                style={{ fontSize: 12, fontWeight: 800, color: softTextColor }}
              >
                {hayFiltrosActivos
                  ? `Filtros activos (${chipsFiltrosActivos.length}):`
                  : `Sin filtros: estás viendo todas las solicitudes del ${ETIQUETA_RANGO_DEFAULT}.`}
              </span>

              {chipsFiltrosActivos.map((chip) => (
                <span
                  key={chip.key}
                  style={{
                    background: chip.deGrafica ? colorSet.azul : colorSet.azulOscuro,
                    color: "#fff",
                    borderRadius: 999,
                    padding: "3px 5px 3px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  title={chip.deGrafica ? "Seleccionado en una gráfica" : "Filtro"}
                >
                  {chip.label}
                  <button
                    type="button"
                    onClick={chip.quitar}
                    title="Quitar este filtro"
                    aria-label={`Quitar ${chip.label}`}
                    style={{
                      border: "none",
                      background: "rgba(255,255,255,0.25)",
                      color: "#fff",
                      borderRadius: 999,
                      width: 20,
                      height: 20,
                      padding: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <IconoX size={10} />
                  </button>
                </span>
              ))}

              {hayFiltrosActivos ? (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  style={{
                    border: `1px solid ${colorSet.azulOscuro}`,
                    background: "transparent",
                    color: isDarkMode ? "#ffffff" : colorSet.azulOscuro,
                    borderRadius: 999,
                    padding: "2px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    cursor: "pointer",
                  }}
                >
                  <IconoX size={10} />
                  Quitar todos
                </button>
              ) : null}

              <button
                type="button"
                className="btn btn-sm btn-primary ms-auto d-inline-flex align-items-center gap-2"
                onClick={() => {
                  setAsesorPanel(null);
                  setPanelAbierto(true);
                }}
              >
                <IconoPersonas />
                Ver desglose por asesor
              </button>
            </div>
            <div style={{ fontSize: 11, color: mutedColor, marginTop: 4 }}>
              Tip: da clic en una barra, rebanada o etapa de cualquier gráfica
              para filtrar <b>todo el tablero</b> con esa selección. Para
              quitarla, vuelve a darle clic o usa la × de su etiqueta.
            </div>
          </div>

          {avisoFiltro ? (
            <div
              key={avisoFiltro.id}
              role="status"
              style={{
                position: "fixed",
                left: "50%",
                bottom: 24,
                transform: "translateX(-50%)",
                zIndex: 2100,
                background: isDarkMode ? "#f9fafb" : "#111827",
                color: isDarkMode ? "#111827" : "#ffffff",
                borderRadius: 12,
                padding: "10px 12px 10px 16px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                display: "flex",
                alignItems: "center",
                gap: 12,
                maxWidth: "min(720px, calc(100vw - 32px))",
                fontSize: 13,
              }}
            >
              <span style={{ minWidth: 0 }}>
                <b>{avisoFiltro.quitado ? "Filtro quitado:" : "Filtro aplicado:"}</b>{" "}
                {avisoFiltro.texto}
                <span style={{ opacity: 0.75 }}>
                  {" "}
                  · todo el tablero se actualizó
                </span>
              </span>
              <button
                type="button"
                className="btn btn-sm btn-light"
                style={{ whiteSpace: "nowrap" }}
                onClick={deshacerUltimoFiltro}
              >
                Deshacer
              </button>
              {!avisoFiltro.quitado ? (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => {
                    setAsesorPanel(null);
                    setPanelAbierto(true);
                    setAvisoFiltro(null);
                  }}
                >
                  Ver asesores
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAvisoFiltro(null)}
                aria-label="Cerrar aviso"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  padding: 4,
                  display: "inline-flex",
                  cursor: "pointer",
                }}
              >
                <IconoX />
              </button>
            </div>
          ) : null}

          <div className="row g-3">
            {shouldShowStatusChart && (
              <div className="col-12 col-xl-4">
                <Card style={cardStyle} className="h-100">
                  <ChartHeader
                    title="Status y substatus de crédito"
                    info={INFO_INDICADORES.statusCredito}
                  />
                  <CardBody>
                    <Chart
                      key={`status-substatus-${chartRenderKey}-${esSinDatos(drill.statusSubstatus) ? "v" : "d"}`}
                      options={drill.statusSubstatus}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "420px" },
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            )}

            {shouldShowContadoStatusChart && (
              <div className="col-12 col-xl-4">
                <Card style={cardStyle} className="h-100">
                  <ChartHeader
                    title="Status de contado"
                    info={INFO_INDICADORES.statusContado}
                  />
                  <CardBody>
                    <Chart
                      key={`contado-status-${chartRenderKey}-${esSinDatos(drill.contadoStatus) ? "v" : "d"}`}
                      options={drill.contadoStatus}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "340px" },
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            )}

            {shouldShowArrendamientoStatusChart && (
              <div className="col-12 col-xl-4">
                <Card style={cardStyle} className="h-100">
                  <ChartHeader
                    title="Status de arrendamiento"
                    info={INFO_INDICADORES.statusArrendamiento}
                  />
                  <CardBody>
                    <Chart
                      key={`arrendamiento-status-${chartRenderKey}-${esSinDatos(drill.arrendamientoStatus) ? "v" : "d"}`}
                      options={drill.arrendamientoStatus}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "340px" },
                      }}
                    />
                  </CardBody>
                </Card>
              </div>
            )}

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Solicitudes por financiera"
                  info={INFO_INDICADORES.solicitudesFinanciera}
                />
                <CardBody style={{ overflowX: "auto" }}>
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th className="black-text" rowSpan={2}>Financiera</th>
                        <th className="black-text text-end" rowSpan={2}>Solicitudes</th>
                        <th className="black-text text-end" rowSpan={2}>Primera opción</th>
                        <th className="black-text text-end" rowSpan={2} title="Se cambió a otra financiera">
                          Cambiadas
                        </th>
                        <th className="black-text text-center" colSpan={4}>
                          Financiera actual (última elegida)
                        </th>
                        <th className="black-text text-end" rowSpan={2} title="Cambiadas + actuales con status Rechazado">
                          Rechazadas total
                        </th>
                        <th className="black-text text-end" rowSpan={2}>% Rechazo</th>
                      </tr>
                      <tr>
                        <th className="black-text text-end">Autorizado</th>
                        <th className="black-text text-end">Rechazado</th>
                        <th className="black-text text-end">Pend. buró</th>
                        <th className="black-text text-end">Otro status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financieraTabla.rows.length ? (
                        financieraTabla.rows.map((row) => (
                          <tr key={row.financiera}>
                            <td style={{ fontWeight: 700 }}>{row.financiera}</td>
                            <td className="text-end">{formatNumber(row.solicitudes)}</td>
                            <td className="text-end">{formatNumber(row.primera)}</td>
                            <td className="text-end">{formatNumber(row.cambiadas)}</td>
                            <td className="text-end" style={{ color: colorSet.turquesa, fontWeight: 700 }}>
                              {formatNumber(row.autorizado)}
                            </td>
                            <td className="text-end" style={{ color: colorSet.rojo, fontWeight: 700 }}>
                              {formatNumber(row.rechazado)}
                            </td>
                            <td className="text-end">{formatNumber(row.pendienteBuro)}</td>
                            <td className="text-end">{formatNumber(row.otro)}</td>
                            <td className="text-end" style={{ fontWeight: 700 }}>
                              {formatNumber(row.rechazadasTotal)}
                            </td>
                            <td className="text-end">{row.pctRechazo}%</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center">
                            Sin datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Solicitudes vs. Afluencia"
                  info={INFO_INDICADORES.afluencia}
                />
                <CardBody>
                  <div className="row g-2 mb-2">
                    {[
                      {
                        label: "Afluencia",
                        value: formatNumber(afluenciaPorAgencia.totalAfluencia),
                        color: colorSet.celeste,
                      },
                      {
                        label: "Solicitudes con seguimiento",
                        value: formatNumber(afluenciaPorAgencia.totalSolicitudes),
                        color: colorSet.azulOscuro,
                      },
                      {
                        label: "% Conversión",
                        value: `${pctConversionAfluencia}%`,
                        color: colorSet.naranja,
                      },
                    ].map((kpi) => (
                      <div key={kpi.label} className="col-12 col-sm-4">
                        <div
                          style={{
                            borderLeft: `4px solid ${kpi.color}`,
                            background: isDarkMode
                              ? "rgba(255,255,255,0.04)"
                              : "#ffffff",
                            borderRadius: 10,
                            padding: "8px 12px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: textColor,
                            }}
                          >
                            {kpi.value}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: mutedColor,
                            }}
                          >
                            {kpi.label}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{ fontSize: 11, color: mutedColor, marginBottom: 6 }}
                  >
                    {FUENTE_AFLUENCIA === "seekop"
                      ? "Afluencia tomada de Seekop."
                      : "Afluencia tomada de los clientes registrados en Babilonia (pendiente conectar Seekop)."}
                  </div>
                  <Chart
                    key={`afluencia-${chartRenderKey}-${esSinDatos(drill.afluencia) ? "v" : "d"}`}
                    options={drill.afluencia}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-md-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Total Solicitudes por Fuente"
                  info={INFO_INDICADORES.fuente}
                />
                <CardBody>
                  <Chart
                    key={`fuente-${chartRenderKey}-${esSinDatos(drill.fuente) ? "v" : "d"}`}
                    options={drill.fuente}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-md-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Total Solicitudes por Financiera"
                  info={INFO_INDICADORES.financiera}
                />
                <CardBody>
                  <Chart
                    key={`financiera-${chartRenderKey}-${esSinDatos(drill.financiera) ? "v" : "d"}`}
                    options={drill.financiera}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-4">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Comparativo Tipo de Pago"
                  info={INFO_INDICADORES.tipoPago}
                />
                <CardBody>
                  <Chart
                    key={`tipo-pago-${chartRenderKey}-${esSinDatos(drill.tipoPago) ? "v" : "d"}`}
                    options={drill.tipoPago}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Venta hecha en Babilonia"
                  info={INFO_INDICADORES.ventaBabilonia}
                />
                <CardBody>
                  <Chart
                    key={`venta-babilonia-${chartRenderKey}-${esSinDatos(drill.ventaBabilonia) ? "v" : "d"}`}
                    options={drill.ventaBabilonia}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Ventas hechas con NR"
                  info={INFO_INDICADORES.ventaNr}
                />
                <CardBody>
                  <Chart
                    key={`venta-nr-${chartRenderKey}-${esSinDatos(drill.ventaNr) ? "v" : "d"}`}
                    options={drill.ventaNr}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Solicitudes tardías"
                  info={INFO_INDICADORES.solicitudesTardias}
                />
                <CardBody>
                  <Chart
                    key={`solicitudes-tardias-${chartRenderKey}-${esSinDatos(drill.solicitudesTardias) ? "v" : "d"}`}
                    options={drill.solicitudesTardias}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Prueba de manejo"
                  info={INFO_INDICADORES.pruebaManejo}
                />
                <CardBody>
                  <Chart
                    key={`prueba-manejo-${chartRenderKey}-${esSinDatos(drill.pruebaManejo) ? "v" : "d"}`}
                    options={drill.pruebaManejo}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Planes más seleccionados"
                  info={INFO_INDICADORES.planes}
                />
                <CardBody>
                  <Chart
                    key={`planes-seleccionados-${chartRenderKey}-${esSinDatos(drill.planes) ? "v" : "d"}`}
                    options={drill.planes}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Top 5 Accesorios / Garantías más seleccionados"
                  info={INFO_INDICADORES.topAccesorios}
                />
                <CardBody>
                  <Chart
                    key={`top-accesorios-garantias-${chartRenderKey}-${esSinDatos(drill.topAccesorios) ? "v" : "d"}`}
                    options={drill.topAccesorios}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Comparativo de ganancias de accesorios y garantías"
                  info={INFO_INDICADORES.ganancias}
                />
                <CardBody>
                  <Chart
                    key={`ganancias-accesorios-garantias-${chartRenderKey}-${esSinDatos(gananciasAccesoriosGarantiasChartOptions) ? "v" : "d"}`}
                    options={gananciasAccesoriosGarantiasChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "390px" },
                    }}
                  />

                  <div className="table-responsive mt-3">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="black-text">Concepto</th>
                          <th className="black-text">
                            {gananciasAccesoriosGarantiasResumen.previousLabel}
                          </th>
                          <th className="black-text">
                            {gananciasAccesoriosGarantiasResumen.currentLabel}
                          </th>
                          <th className="black-text">Diferencia</th>
                          <th className="black-text">Productos vendidos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gananciasAccesoriosGarantiasRows.map((row) => (
                          <tr key={`ganancia-row-${row.key}`}>
                            <td>{row.label}</td>
                            <td>{formatCurrency(row.previous)}</td>
                            <td>{formatCurrency(row.current)}</td>
                            <td>
                              {formatCurrency(row.diff)} ({row.diffPct}%)
                            </td>
                            <td>
                              {formatNumber(row.currentCount)} actual /{" "}
                              {formatNumber(row.previousCount)} anterior
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-xl-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Total Solicitudes por Agencia y Departamento"
                  info={INFO_INDICADORES.agenciaDepartamento}
                />
                <CardBody>
                  <Chart
                    key={`agencia-departamento-${chartRenderKey}-${esSinDatos(drill.agenciaDepartamento) ? "v" : "d"}`}
                    options={drill.agenciaDepartamento}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "380px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div
              className={
                shouldShowCreditOnlyBlocks ? "col-12 col-xl-6" : "col-12"
              }
            >
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Acumulado Mes Actual y Acumulado Mes Anterior por Día"
                  info={INFO_INDICADORES.acumulado}
                />
                <CardBody>
                  <Chart
                    key={`acumulado-${chartRenderKey}-${esSinDatos(acumuladoChartOptions) ? "v" : "d"}`}
                    options={acumuladoChartOptions}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "360px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-xl-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader title={`Histórico mensual de solicitudes ${anioHistorico}`} info={INFO_INDICADORES.historicoMensual} />
                <CardBody>
                  <div className="d-flex justify-content-end align-items-center gap-2 mb-1">
                    {loadingAnual ? (
                      <span style={{ fontSize: 11, color: mutedColor }}>
                        Cargando año…
                      </span>
                    ) : null}
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={anioHistorico}
                      onChange={(e) => setAnioHistorico(Number(e.target.value))}
                    >
                      {opcionesAnio.map((anio) => (
                        <option key={anio} value={anio}>
                          {anio}
                        </option>
                      ))}
                    </select>
                  </div>
                  {historicoSolicitado ? (
                    <Chart
                      key={`historico-mensual-${chartRenderKey}-${esSinDatos(drill.historicoMensual) ? "v" : "d"}`}
                      options={drill.historicoMensual}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "360px" },
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-center"
                      style={{ height: 360, gap: 10, color: mutedColor }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, maxWidth: 320 }}>
                        El histórico del año no se carga al abrir el tablero
                        para que sea más rápido.
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setHistoricoSolicitado(true)}
                      >
                        Cargar histórico {anioHistorico}
                      </button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-xl-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader title={`% de aprobación mensual ${anioHistorico}`} info={INFO_INDICADORES.aprobacionMensual} />
                <CardBody>
                  <div className="d-flex justify-content-end align-items-center gap-2 mb-1">
                    {loadingAnual ? (
                      <span style={{ fontSize: 11, color: mutedColor }}>
                        Cargando año…
                      </span>
                    ) : null}
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={anioHistorico}
                      onChange={(e) => setAnioHistorico(Number(e.target.value))}
                    >
                      {opcionesAnio.map((anio) => (
                        <option key={anio} value={anio}>
                          {anio}
                        </option>
                      ))}
                    </select>
                  </div>
                  {historicoSolicitado ? (
                    <Chart
                      key={`aprobacion-mensual-${chartRenderKey}-${esSinDatos(drill.aprobacionMensual) ? "v" : "d"}`}
                      options={drill.aprobacionMensual}
                      immutable={true}
                      containerProps={{
                        style: { width: "100%", height: "360px" },
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-center"
                      style={{ height: 360, gap: 10, color: mutedColor }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, maxWidth: 320 }}>
                        El histórico del año no se carga al abrir el tablero
                        para que sea más rápido.
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setHistoricoSolicitado(true)}
                      >
                        Cargar histórico {anioHistorico}
                      </button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>

            {shouldShowCreditOnlyBlocks && (
              <>
                <div className="col-12 col-lg-4">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Total Solicitudes por Estatus"
                      info={INFO_INDICADORES.statusSimple}
                    />
                    <CardBody>
                      <Chart
                        key={`status-simple-${chartRenderKey}-${esSinDatos(drill.statusSimple) ? "v" : "d"}`}
                        options={drill.statusSimple}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "340px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-lg-4">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Subestatus General"
                      info={INFO_INDICADORES.substatus}
                    />
                    <CardBody>
                      <Chart
                        key={`substatus-simple-${chartRenderKey}-${esSinDatos(drill.substatus) ? "v" : "d"}`}
                        options={drill.substatus}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "380px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>
              </>
            )}

            <div
              className={
                shouldShowCreditOnlyBlocks ? "col-12 col-lg-4" : "col-12"
              }
            >
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Embudo de Solicitudes"
                  info={INFO_INDICADORES.funnel}
                />
                <CardBody>
                  <EmbudoSolicitudes
                    stages={embudoStages}
                    footer={embudoFooter}
                    textColor={textColor}
                    mutedColor={mutedColor}
                    onStageClick={onEmbudoClick}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Tendencia de Solicitudes por Día"
                  info={INFO_INDICADORES.tendencia}
                />
                <CardBody>
                  <Chart
                    key={`tendencia-${chartRenderKey}-${esSinDatos(drill.tendencia) ? "v" : "d"}`}
                    options={drill.tendencia}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "340px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            <div className="col-12 col-lg-6">
              <Card style={cardStyle} className="h-100">
                <ChartHeader
                  title="Top 10 Agencias por Solicitudes"
                  info={INFO_INDICADORES.topAgencias}
                />
                <CardBody>
                  <Chart
                    key={`top-agencias-${chartRenderKey}-${esSinDatos(drill.topAgencias) ? "v" : "d"}`}
                    options={drill.topAgencias}
                    immutable={true}
                    containerProps={{
                      style: { width: "100%", height: "400px" },
                    }}
                  />
                </CardBody>
              </Card>
            </div>

            {shouldShowCreditOnlyBlocks && (
              <>
                <div className="col-12 col-lg-6">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Top 10 Agencias por Formalización"
                      info={INFO_INDICADORES.topAgenciasFormalizacion}
                    />
                    <CardBody>
                      <Chart
                        key={`top-formalizacion-${chartRenderKey}-${esSinDatos(drill.topAgenciasFormalizacion) ? "v" : "d"}`}
                        options={drill.topAgenciasFormalizacion}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "400px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Autorización vs Formalización por Agencia"
                      info={INFO_INDICADORES.conversionAgencia}
                    />
                    <CardBody>
                      <Chart
                        key={`conversion-agencia-${chartRenderKey}-${esSinDatos(drill.conversionAgencia) ? "v" : "d"}`}
                        options={drill.conversionAgencia}
                        immutable={true}
                        containerProps={{
                          style: { width: "100%", height: "400px" },
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-xl-8">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Asesor"
                      info={INFO_INDICADORES.asesor}
                    />
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>
                            <th className="black-text">Total Solicitudes</th>
                            <th className="black-text">Tardías</th>
                            <th className="black-text">Crédito</th>
                            <th className="black-text">Contado</th>
                            <th className="black-text">Arrendamiento</th>
                            <th className="black-text">Autorizadas</th>
                            <th className="black-text">% Autorizadas</th>
                            <th className="black-text">Rechazadas</th>
                            <th className="black-text">% Rechazadas</th>
                            <th className="black-text">Formalizadas</th>
                            <th className="black-text">Pruebas manejo</th>
                            <th className="black-text">Pendientes Buró</th>
                            <th className="black-text">% Participación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {asesorRows.length > 0 ? (
                            asesorRows.map((row, index) => (
                              <tr
                                key={row.asesor}
                                onClick={() => abrirClientesAsesor(row.asesor)}
                                style={{ cursor: "pointer" }}
                                title="Ver clientes de este asesor"
                              >
                                <td>{index + 1}</td>
                                <td>{row.asesor}</td>
                                <td>{row.total}</td>
                                <td>
                                  {row.tardias > 0 ? (
                                    <span
                                      style={{ color: "#d9822b", fontWeight: 700 }}
                                    >
                                      {row.tardias}
                                    </span>
                                  ) : (
                                    0
                                  )}
                                </td>
                                <td>{row.creditos}</td>
                                <td>{row.contados}</td>
                                <td>{row.arrendamientos}</td>
                                <td>{row.autorizadas}</td>
                                <td>
                                  {row.pctAutorizadas}% ({row.autorizadas} de{" "}
                                  {row.creditos})
                                </td>
                                <td>{row.rechazadas}</td>
                                <td>
                                  {row.pctRechazadas}% ({row.rechazadas} de{" "}
                                  {row.creditos})
                                </td>
                                <td>{row.formalizadas}</td>
                                <td>{row.pruebasManejo}</td>
                                <td>{row.pendientesBuro}</td>
                                <td>
                                  {row.pctParticipacion}% ({row.total})
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="15" className="text-center">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12 col-xl-4">
                  <div className="row g-3">
                    <div className="col-12">
                      <Card style={cardStyle} className="h-100">
                        <ChartHeader
                          title="Total Formalizaciones por Financiera"
                          info={INFO_INDICADORES.formalizacionesFinanciera}
                        />
                        <CardBody>
                          <Chart
                            key={`formalizaciones-financiera-${chartRenderKey}-${esSinDatos(drill.formalizacionesFinanciera) ? "v" : "d"}`}
                            options={drill.formalizacionesFinanciera}
                            immutable={true}
                            containerProps={{
                              style: { width: "100%", height: "320px" },
                            }}
                          />
                        </CardBody>
                      </Card>
                    </div>

                    <div className="col-12">
                      <Card style={cardStyle} className="h-100">
                        <ChartHeader
                          title="Dirección"
                          info={INFO_INDICADORES.direccion}
                        />
                        <CardBody style={{ overflowX: "auto" }}>
                          <table className="table table-sm table-hover align-middle mb-0">
                            <thead>
                              <tr>
                                <th className="black-text">Dirección</th>
                                <th className="black-text">Total</th>
                                <th className="black-text">Formalizaciones</th>
                                <th className="black-text">% De Aprobación</th>
                              </tr>
                            </thead>
                            <tbody>
                              {direccionRows.length > 0 ? (
                                direccionRows.map((row) => (
                                  <tr
                                    key={row.direccion}
                                    onClick={() =>
                                      aplicarSeleccionGrafica([
                                        { dim: "direccion", value: row.direccion },
                                      ])
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td>{row.direccion}</td>
                                    <td>{row.total}</td>
                                    <td>{row.formalizaciones}</td>
                                    <td>
                                      {row.pctAprobacion}% ({row.autorizadas} de{" "}
                                      {row.total})
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">
                                    Sin datos
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </div>

                    <div className="col-12">
                      <Card style={cardStyle} className="h-100">
                        <ChartHeader
                          title="Razón Social"
                          info={INFO_INDICADORES.razonSocial}
                        />
                        <CardBody style={{ overflowX: "auto" }}>
                          <table className="table table-sm table-hover align-middle mb-0">
                            <thead>
                              <tr>
                                <th className="black-text">Razón social</th>
                                <th className="black-text">Total</th>
                                <th className="black-text">Formalizaciones</th>
                                <th className="black-text">% De Aprobación</th>
                              </tr>
                            </thead>
                            <tbody>
                              {razonSocialRows.length > 0 ? (
                                razonSocialRows.map((row) => (
                                  <tr
                                    key={row.razonSocial}
                                    onClick={() =>
                                      aplicarSeleccionGrafica([
                                        {
                                          dim: "razonSocial",
                                          value: row.razonSocial,
                                        },
                                      ])
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td>
                                      <div style={{ fontWeight: 600 }}>
                                        {row.razonSocial}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: mutedColor,
                                        }}
                                      >
                                        {row.grupo} · {row.direccion}
                                      </div>
                                    </td>
                                    <td>{row.total}</td>
                                    <td>{row.formalizaciones}</td>
                                    <td>
                                      {row.pctAprobacion}% ({row.autorizadas} de{" "}
                                      {row.creditos})
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center">
                                    Sin datos
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Ranking de asesores por paquetes vendidos"
                      info={INFO_INDICADORES.rankingPaquetes}
                    />
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>
                            <th className="black-text">Elite</th>
                            <th className="black-text">Gold</th>
                            <th className="black-text">Platinum</th>
                            <th className="black-text">Plan cliente</th>
                            <th className="black-text">
                              Total paquetes vendidos
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {asesorPaquetesRows.length > 0 ? (
                            asesorPaquetesRows.map((row, index) => (
                              <tr key={`asesor-paquetes-${row.asesor}`}>
                                <td>{index + 1}</td>
                                <td>{row.asesor}</td>
                                <td>{row.Elite || 0}</td>
                                <td>{row.Gold || 0}</td>
                                <td>{row.Platinum || 0}</td>
                                <td>{row["Plan cliente"] || 0}</td>
                                <td>{row.totalPaquetesVendidos}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>

                <div className="col-12">
                  <Card style={cardStyle} className="h-100">
                    <ChartHeader
                      title="Ranking de asesores por accesorios y garantías vendidos"
                      info={INFO_INDICADORES.rankingAccesorios}
                    />
                    <CardBody style={{ overflowX: "auto" }}>
                      <table className="table table-sm align-middle mb-0">
                        <thead style={{ position: "sticky", top: 0 }}>
                          <tr>
                            <th className="black-text">#</th>
                            <th className="black-text">Asesor</th>

                            {accesoriosGarantiasColumns.map((col) => (
                              <th
                                key={`col-${col.key}`}
                                className="black-text"
                                title={`${col.title} (${col.type})`}
                              >
                                {truncateText(col.title, 24)}
                              </th>
                            ))}

                            <th className="black-text">Total items vendidos</th>
                          </tr>
                        </thead>

                        <tbody>
                          {asesorAccesoriosGarantiasMatrixRows.length > 0 ? (
                            asesorAccesoriosGarantiasMatrixRows.map(
                              (row, index) => (
                                <tr key={`asesor-matrix-${row.asesor}`}>
                                  <td>{index + 1}</td>
                                  <td>{row.asesor}</td>

                                  {accesoriosGarantiasColumns.map((col) => (
                                    <td key={`cell-${row.asesor}-${col.key}`}>
                                      {row[col.key] || 0}
                                    </td>
                                  ))}

                                  <td>{row.totalItemsVendidos}</td>
                                </tr>
                              ),
                            )
                          ) : (
                            <tr>
                              <td
                                colSpan={accesoriosGarantiasColumns.length + 3}
                                className="text-center"
                              >
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <PanelDesglose
        open={panelAbierto}
        onClose={() => {
          setPanelAbierto(false);
          setAsesorPanel(null);
        }}
        titulo="Desglose por asesor"
        chips={chipsActivos}
        asesores={desgloseAsesores}
        asesorSeleccionado={asesorPanel}
        onSelectAsesor={setAsesorPanel}
        clientes={clientesAsesor}
        isDarkMode={isDarkMode}
        textColor={textColor}
        mutedColor={mutedColor}
        gridColor={gridColor}
        colorSet={colorSet}
      />
    </div>
  );
}

export default DashboardRendimiento;